/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { floorBoundOf } from '../../utils/floor-bound';
import type { SavedLibraryEntry } from '../shared/editors/DecoratorExpressionEditor';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Input,
  Text,
  Textarea,
  VStack,
  Badge,
  createListCollection,
  NativeSelect,
  Select,
  Spinner,
  Switch,
  Portal,
} from '@chakra-ui/react';
import { NumberField } from '../shared/inputs/NumberField';
import {
  useCloneDemandSource,
  useCreateDemandSource,
  useGuardedUpdateDemandSource,
  useUsers,
  usePixels,
  useCreatePixel,
  useSystemDefaults,
} from '../../hooks';
import { useAuth } from '../../hooks';
import { demandSourceService } from '../../services';
import { useCreateExpression, useExpressionNameLookup } from '../../hooks/useExpressionLibrary';
import type {
  DemandSource,
  DemandSourceMethod,
  DemandSourceValidation,
  PrebidConfig,
  CustomPixel,
  ReusablePixel,
} from '../../types';
import { OrtbBodyEditor } from '../shared/editors/OrtbBodyEditor';
import {
  buildRequestOccurrences,
  replaceValueInBody,
  replaceValueInHeaders,
  replaceValueInParams,
} from '../shared/editors/request-occurrences';
import { withoutRecordedVersions } from '../shared/editors/shared-expression-version';
import { templateToLeaves, type MappingLeaf } from '../shared/editors/ortb-body-editor.adapters';
import { unfilledOperatorSlots } from '../../utils/demand-source-coverage';
import {
  describeTemplateContents,
  TEMPLATE_DISCLOSURE_HEADLINE,
  type TemplateDisclosureLine,
} from '../shared/template-disclosure';
import type { MacroExpression } from '../../types/decorator-expression';
import { parseSlot } from '@artifact-authoring';
import { ResponseMappingEditor } from '../shared/editors/ResponseMappingEditor';
import { RESPONSE_INTRO, validateResponseMapping } from '../shared/editors/response-field-catalog';
import { validatePixels } from '../shared/editors/pixel-editor.adapters';
import { ArtifactIdentity } from '../shared/ArtifactIdentity';
import { ConfirmDialog } from './ConfirmDialog';
import { CloneArtifactOverlay } from '../shared/CloneArtifactDialog';
import { DEMAND_SOURCE_OUTCOMES } from '../shared/clone-options';
import { TemplateDisclosureList } from '../shared/TemplateDisclosureList';
import { useUnsavedWork } from './UnsavedWorkGuard';
import {
  disambiguateName,
  proposeArtifactName,
  ARTIFACT_ID_CONSTRAINT_HINT,
  ARTIFACT_ID_FROZEN_HELP,
  ARTIFACT_ID_FROZEN_HINT,
  artifactIdPurpose,
  artifactTitle,
} from '../../utils/artifact-identity';
import { useArtifactLineage } from '../../hooks/useArtifactLineage';
import {
  DEMAND_SOURCE_INHERITANCE,
  useArtifactInheritance,
  useDemandSource,
  useRevertArtifactValues,
  useStandardTerms,
} from '../../hooks';
import type { ArtifactInheritance } from '../../types';
import { InheritanceMark } from '../shared/InheritanceMark';
import { DraftSectionMark } from '../shared/DraftSectionMark';
import { useCopiedFromTemplate, type CopiedFromTemplate } from '../../hooks/useCopiedFromTemplate';
import { pathInheritance } from '../../utils/artifact-inheritance';
import { termsFormFromStored, type TermsFormValue } from '../shared/editors/terms-editor.adapters';
import type { TermMember } from '@artifact-terms';
import { validateHeaders } from '../shared/editors/headers-editor.adapters';
import { validateParams } from '../shared/editors/parameter-editor.adapters';
import { validateOrtbBody } from '../shared/editors/ortb-body-validation';
import { worstLevel, type MappingIssueLevel } from '../shared/editors/mapping-issues';
import { IssueDot } from '../shared/editors/ValidationSummary';
import { detectTypeFromUrl } from '../../utils/demand-source-utils';
import { useRequestPreview, type RequestPreview } from '../../hooks/useRequestPreview';
import { useTestContext } from '../../hooks/useTestContext';
import { TestContextPicker } from '../shared/TestContextPicker';
import { validationDetail, validationLabel } from '../../utils/validation';
import { QuickTestPanel } from './QuickTestPanel';
import { deriveTestOutcome, type DiagnosisFix, type TestOutcome } from '../../utils/test-diagnosis';
import type { QuickTestResult, TestRunContext, DemandSourceTestEvidence } from '../../services/demand-source.service';
import { OwnerControl } from './OwnerControl';
import { useVisibilityChange } from './useVisibilityChange';
import { FormField, type FieldFinding } from '../shared/inputs/FormField';
import { artifactService } from '../../services/artifact.service';
import type { DriverCapture } from '../shared/RequestResponseInspector';
import type { MappingDocument } from '../../types/mapping';
import type { ResponseMappingPreview } from '../../types/response-mapping-preview';
import {
  PixelEditor,
  TemplateSelector,
  paramsDtoToRows,
  rowsToParamsDto,
  TermsEditor,
  rowsToDtoCustomPixels,
  termsToPayload,
  restrictionsToPayload,
  termsFormProblem,
  inheritedFromResolved,
  inheritedFromStandardTerms,
  type InheritedTerms,
  HeadersEditor,
  headersObjectToRows,
  rowsToHeadersObject,
  rowsToHeadersProvenance,
  CollapsibleEditorSection,
  RequestParameterEditor,
  type ParameterRow,
  type HeaderRow,
  type DemandSourceTermsPayload,
} from '../shared';
import type { ValueOrigin } from '../shared/editors/parameter-editor.adapters';
import {
  hasUnfilledEndpointPlaceholder,
  unfilledEndpointPlaceholders,
  sanitizeOrtbOutboundMapping,
  shouldConfirmClose,
  liftEndpointQuery,
  buildRowsFromDetectedParameters,
  WIZARD_STEP_FOR_FIX_TARGET,
  wouldLoseWork,
} from './SimplifiedDemandSourceWizard.helpers.ts';
import {
  applyTemplateToForm,
  templateSuppliedRows,
  DEFAULT_FORM,
  formFromDemandSource,
  prebidConfigFromForm,
  PROTOCOL_VERSIONS,
  seedOrtbBodyTemplate,
  toMethod,
  type PrebidBidderRow,
  type WizardForm,
} from './SimplifiedDemandSourceWizard.form';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

/**
 * The id a name suggests — **a suggestion, never a derivation** (A7, `#1147`).
 *
 * Returns nothing once the operator has typed in the ID box, so the two stop being coupled the
 * moment they have said what they want. Sharing the slug shape with the strategy editor's validator
 * (`^[a-z0-9-]+$`) means the suggestion is always accepted by it.
 */
function suggestIdFrom(name: string): { id?: string } {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? { id: slug } : {};
}

/** URL placeholder hint per demand-source type */
const URL_PLACEHOLDERS: Record<DemandSourceMethod, string> = {
  VAST: 'https://ad-server.example.com/vast?cb={cb}',
  ORTB: 'https://exchange.example.com/openrtb/bid',
  API: 'https://api.example.com/ads',
};

const TYPE_LABELS: Record<DemandSourceMethod, string> = {
  VAST: 'VAST',
  ORTB: 'OpenRTB',
  API: 'Custom',
};

/**
 * The four phases (#896 · R1). One flow whose depth follows the endpoint, not a
 * mode the user has to predict before seeing what their endpoint requires.
 *
 * The order is not cosmetic. **Everything authored in phases 1–3 is
 * request-affecting and demotes Proven (R2a); nothing in phase 4 is.** Endpoint,
 * protocol, version and timeout in Connection, headers/params/body in Configure
 * Request, response mapping in Prove & map — R2a's demote list exactly. Name, description,
 * owner, visibility, terms, weight, floor CPM and max fetch rounds are its
 * does-not-demote list, and they sit after the proof for the same reason: they are
 * judgments about a thing whose existence is not yet established.
 *
 * That invariant is what makes the flow legible — everything before Prove & map can
 * invalidate the proof, nothing after it can — and it hands #897 its "which
 * saved fields are request-affecting" answer without a second list to maintain.
 *
 * **The names are shared with the detail page (#995 · R11, and literally since
 * `ST11`).** Phase 1 authors exactly what that page's **Connection** section shows, so it is
 * *called* Connection — the two surfaces name one concept with one word rather than with two the
 * detail page had to translate between. Two consequences that were
 * previously the misalignment:
 *
 * - **Version and timeout live here, with the protocol** (R13). A version is a
 *   property of a protocol; authoring them a step apart was the sharpest instance
 *   of the disagreement, and it is why version was missing from the detail page
 *   altogether. Surfacing version early is also what lets Connect offer the right
 *   parameter catalog — §3a makes it catalog-driving.
 * - **Response mapping is named in the rail** (R15). It sat inside a step called
 *   "Prove", which is why it read as hidden. It is deliberately *not* split into
 *   its own phase: #992 made testing and mapping one action, and a step boundary
 *   through a single activity would re-create the duplicate-test problem it removed.
 */
const PHASES = ['Connection', 'Configure Request', 'Prove & map', 'Name and save'] as const;
/** The phases that carry the test panel (`#947`): every one that can change what a test sends. */
const TESTABLE_PHASES: ReadonlySet<string> = new Set<(typeof PHASES)[number]>([
  'Configure Request',
  'Prove & map',
  'Name and save',
]);

export type StepLabel = (typeof PHASES)[number];

/**
 * The question each phase answers. Shown under the active phase in the rail —
 * the phases are named for what the operator is deciding, and the question is
 * what makes that legible without a manual.
 */
const PHASE_QUESTION: Record<StepLabel, string> = {
  Connection: 'What am I connecting to, and how?',
  'Configure Request': 'What will be sent?',
  'Prove & map': 'Does it work, and what came back?',
  'Name and save': 'What am I creating, in what state?',
};

/**
 * The protocol picker's values are `DemandSourceMethod`, checked by `satisfies`.
 *
 * They were lowercase after #936, so `value={[form.method]}` (`['VAST']`) matched no
 * item and the control rendered **blank**; picking an option then wrote `'vast'` into
 * the form, and `PROTOCOL_VERSIONS['vast']` is `undefined`, so the phase threw on the
 * next render and the choice appeared not to stick. Neither was visible to `tsc`: the
 * collection was untyped and the handler cast its value.
 */
const typeCollection = createListCollection({
  items: [
    { label: 'VAST', value: 'VAST' },
    { label: 'OpenRTB', value: 'ORTB' },
    { label: 'Custom', value: 'API' },
  ] satisfies { label: string; value: DemandSourceMethod }[],
});

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Parse a raw JSON string as an object; returns null if empty, throws if invalid. */

/**
 * What blocks moving on from a phase. Deliberately thin: only values that make
 * the *next* phase impossible or nonsensical. Everything else warns and lets the
 * operator continue (#875, R2) — this gate decides what is unusable, not what is
 * incomplete.
 */
/**
 * **Name the fields, not the mechanism** (`#1045`).
 *
 * *"2 slots are unfilled"* names our vocabulary; an operator needs to know which boxes to type in
 * and where. The plane is part of that — `pubid` in the query parameters and `pubid` in the request
 * body are two different boxes.
 */
function describeUnfilledSlots(slots: readonly string[]): string {
  const named = slots.map((slot) => {
    const parsed = parseSlot(slot);
    if (!parsed) return slot;
    if (parsed.plane === 'endpoint') return 'the endpoint URL';
    const where = { param: 'parameter', header: 'header', body: 'request body' }[parsed.plane];
    return `${parsed.address ?? ''} (${where})`;
  });
  const list = named.slice(0, 4).join(', ');
  const rest = named.length > 4 ? `, and ${named.length - 4} more` : '';
  return `Fill in the values only you can supply — your ad server gave you these: ${list}${rest}.`;
}

/**
 * **What is wrong, without deciding what the operator may do about it** (ratified by Todd,
 * 2026-08-27).
 *
 * These two were **blocking gates** until today — an unfilled `{placeholder}` in the endpoint, and a
 * declared operator-supplied slot with nothing in it. Both were demoted, and the argument that
 * settled it was measured rather than reasoned:
 *
 * > **Execution never throws, never refuses, and never blocks.** A required VAST parameter with
 * > nothing in it goes out as `pubid=`; an endpoint still carrying `{pubid}` is sent to a perfectly
 * > reachable host with the braces intact; an ORTB row simply does not appear. In all three cases
 * > the request is made.
 *
 * So the console was **stricter than the runtime**: the product would serve this configuration in
 * production and would not let you *test* it. That is backwards, and worse than inconsistent — Prove
 * is the harness, and refusing to run it withholds the one piece of evidence that settles the
 * question. Todd: *"It's not our job to stop the user from doing something wrong. It's our job to
 * tell them they are."*
 *
 * **Blocking is reserved for structurally impossible**, which is a short list on purpose: no
 * endpoint, an endpoint that is not a URL, and identity. If we block test, we block save — errors
 * and warnings block neither.
 *
 * **Templates remain the exception**, and for the reason they always were: they are often
 * deliberately unfinished, so an unfilled slot is the shape rather than a defect. It is not even an
 * advisory there.
 *
 * The fear the gate was built on is real and is answered elsewhere: a source tested with an unfilled
 * placeholder no-bids *"for a reason that has nothing to do with its configuration"*. That is `R3` —
 * **failure leads with a diagnosis** — and the fix is the sentence on the failure, not a locked door.
 */
export function stepAdvisories(
  step: number,
  form: WizardForm,
  steps: readonly StepLabel[],
  declaredAsShape = false,
): string[] {
  if (declaredAsShape) return [];
  const label = steps[step];
  const out: string[] = [];

  // **Only what belongs to no one field is here** (`3h`, `#1456`). The Connection, Prebid and Terms
  // findings sit beside their controls now ({@link formFieldFindings}, `termsFieldFindings`), where
  // the detail page shows them too. What is left is about rows in the mapping editors, which carry
  // their own marks; this sentence is the phase's summary of them.
  if (label === 'Prove & map') {
    const sources = {
      endpoint: form.endpoint,
      parameterRows: form.parameterRows,
      headerRows: form.headerRows,
      bodyLeaves: templateToLeaves(form.ortbBodyTemplate),
    };
    // **The form's own claims, read from the planes that hold them** (`#1184`). No side-car is
    // consulted: a preset's `from: 'operator'` declaration materialises as a row with
    // `optional: false`, and all eleven shipped ones are `importance: 'required'` — measured
    // 2026-08-27, zero operator-supplied fields that are not also required. So the row already says
    // it, and reading a second source would be two answers to one question.
    const unfilled = unfilledOperatorSlots(null, sources);
    if (unfilled.length > 0) out.push(describeUnfilledSlots(unfilled));

    // Todd's case, 2026-08-27: *"Oh shoot, I have no idea what the url should be, and John who
    // knows won't be back until Thursday."* A pixel with no URL is unfinished, not invalid — and
    // where it is genuinely arguable, the ratified fallback is **error, not block**.
    const emptyPixels = form.pixelRows.filter((pixel) => !pixel.urlTemplate.trim()).length;
    if (emptyPixels > 0) {
      out.push(
        `${emptyPixels} custom pixel${emptyPixels === 1 ? ' has' : 's have'} no URL, so ${emptyPixels === 1 ? 'it' : 'they'} will not fire.`,
      );
    }
  }

  return out;
}

/** The form fields a non-blocking finding can sit beside, outside the Terms box (`#1456`). */
export type FormFindingField = 'endpoint' | 'timeout' | 'prebid';

/**
 * **The non-blocking findings on the Connection and Prebid fields, for every surface that edits
 * them** (`3h`, `#1456`).
 *
 * They were phase advisories: a red box at the end of the wizard's step that copied the blocking box
 * and sat beside no field, and the detail page, which edits the same fields through the same controls,
 * showed none of them — an empty timeout saved in place with no signal. Now one function answers for
 * both, and each finding names its field, so the shared control draws it beside itself.
 *
 * Missing and wrong, never invalid: an invalid value is {@link stepProblems}' and blocks. The level
 * says how sure we are it will not do what was meant — an endpoint still carrying a placeholder and a
 * Prebid config with nothing to run are **errors** (the request cannot be what the operator meant); an
 * empty timeout is a **warning** (the driver's own applies, which may well be fine).
 *
 * A template is exempt, as it always was: it is often deliberately unfinished, so an unfilled slot is
 * its shape rather than a defect (`declaredAsShape`).
 */
export function formFieldFindings(
  form: WizardForm,
  declaredAsShape: boolean,
): Partial<Record<FormFindingField, FieldFinding>> {
  if (declaredAsShape) return {};
  const out: Partial<Record<FormFindingField, FieldFinding>> = {};

  if (hasUnfilledEndpointPlaceholder(form.endpoint)) {
    // The hole, not the first brace: a macro before it (`{max_duration}`) is not the one to replace.
    const [placeholder] = unfilledEndpointPlaceholders(form.endpoint);
    out.endpoint = {
      level: 'error',
      message: `Replace ${placeholder ?? 'the placeholder'} in the URL with the value your ad server gave you.`,
    };
  }

  if (form.timeout === undefined) {
    out.timeout = {
      level: 'warn',
      message: 'Timeout has no value — the driver will fall back to its own, which may not be what you want.',
    };
  }

  if (form.method === 'ORTB' && form.prebidEnabled) {
    const hasStoredRequest = form.prebidStoredRequestId.trim() !== '';
    const namedBidders = form.prebidBidderRows.filter((r) => r.name.trim() !== '');
    if (!hasStoredRequest && namedBidders.length === 0) {
      out.prebid = {
        level: 'error',
        message:
          'Prebid Server has no bidder adapter and no auction-level stored-request ID, so it has nothing to run.',
      };
    }
  }

  return out;
}

/**
 * **Which control a blocking problem belongs to** (`ST12` item 4, `#1359`).
 *
 * `validateStep` returned one string for the whole phase, so a refused save rendered a sentence at
 * the footer **attached to nothing** — and `CloneArtifactDialog`, one component away, already did the
 * right thing with the reasoning in a comment: *a red message beside a field nobody has touched
 * reads as a failure rather than as a rule.*
 *
 * A closed union rather than a `string`, so a problem cannot be raised for a control that does not
 * exist: a record keyed by `string` cannot report a missing member (`#1404`). `terms` is here for
 * completeness and is rendered by `TermsEditor`, which already marks its own members.
 */
export type WizardFieldKey = 'endpoint' | 'timeout' | 'prebid' | 'name' | 'id' | 'terms';

export interface WizardProblem {
  field: WizardFieldKey;
  message: string;
}

/**
 * **Every blocking problem on a step, with the control each belongs to** (`ST12` item 4).
 *
 * The order is the order the operator reads the phase in, because {@link validateStep} takes the
 * first as *the* reason the step is refused and the footer says it — so the sentence an operator is
 * given must name the first thing they would fix.
 *
 * `existingNames` is required and nullable (`#1134`): `null` is a caller with no list, and the name
 * collision check is simply not made. It is a **visible** collision only — a client holds a
 * visibility-filtered slice of the store, so the server stays the authority (`ST12` item 3).
 */
export function stepProblems(
  step: number,
  form: WizardForm,
  steps: readonly StepLabel[],
  existingNames: readonly string[] | null,
): readonly WizardProblem[] {
  const label = steps[step];
  const out: WizardProblem[] = [];

  if (label === 'Connection') {
    if (!form.endpoint.trim()) {
      out.push({ field: 'endpoint', message: 'URL / Endpoint is required.' });
    } else {
      // **Any scheme parses, so parsing is not the test** (`3h` rehearsal): `p://proxy:3000/vast` is a
      // valid URL to `new URL` and a request no driver can make. Both drivers call over HTTP(S).
      let protocol: string | null = null;
      try {
        protocol = new URL(form.endpoint.trim()).protocol;
      } catch {
        protocol = null;
      }
      if (protocol !== 'http:' && protocol !== 'https:') {
        out.push({ field: 'endpoint', message: 'Enter a URL starting with http:// or https://.' });
      }
    }
    // **An entered number outside its range is invalid; an empty box is merely missing** (`R3a`).
    // The guard reads `!== undefined` rather than relying on a comparison, because `undefined < 500`
    // is `false` — the same trap `NumberField`'s own rule names, arriving from the other side now
    // that absence is an advisory rather than a gate.
    if (form.timeout !== undefined && (form.timeout < 500 || form.timeout > 30000)) {
      out.push({ field: 'timeout', message: 'Timeout must be between 500 and 30000 ms.' });
    }
  }

  // Configure Request — request-shaping values only. Headers and parameter rows are always
  // structurally valid (structured editors), so only the Prebid section can fail here.
  if (label === 'Configure Request' && form.method === 'ORTB' && form.prebidEnabled) {
    const validBidderRows = form.prebidBidderRows.filter((r) => r.name.trim() !== '');
    // Missing configuration is an **advisory**; JSON that does not parse is an entered value that
    // fails its own validator, and that still blocks (`R3a`).
    for (const row of validBidderRows) {
      const problem = bidderParamsProblem(row.paramsJson);
      if (problem) out.push({ field: 'prebid', message: `Bidder "${row.name}": ${problem}` });
    }
  }

  // **Prove & map blocks on nothing** (`R3a`). A response mapping is read from a payload the
  // operator is looking at, and a pixel with no URL is unfinished rather than invalid — both are
  // advisories now. Left as a comment rather than an empty branch so the absence reads as a
  // decision instead of an oversight.

  // Name and save — identity is deferred to here precisely so it is judged about
  // something that exists, but a source still cannot be saved without a name.
  if (label === 'Name and save') {
    const name = form.name.trim();
    if (!name) out.push({ field: 'name', message: 'Name is required.' });
    else if (name.length < 2) out.push({ field: 'name', message: 'Name must be at least 2 characters.' });
    else if (existingNames?.some((other) => other.trim().toLowerCase() === name.toLowerCase())) {
      // **A duplicate name is in CLAUDE.md's blocking list**, and until here the wizard was the one
      // surface of three that did not check — the one an operator meets first, and the only one that
      // found out at save. It is deliberately a check against what this viewer can **see**: the store
      // is bigger than the list, and the server refuses the rest.
      out.push({ field: 'name', message: `You already have a demand source called "${name}".` });
    }
    // A7 (#1147): the id is authored, and the same shape a strategy's is — it is referenced by
    // callers, so it has to be URL-safe. Suggested from the name, so this only fires on an id the
    // operator cleared or typed over.
    if (!form.id.trim()) out.push({ field: 'id', message: 'ID is required.' });
    else if (!/^[a-z0-9-]+$/.test(form.id.trim()))
      out.push({ field: 'id', message: 'ID must be lowercase letters, numbers and hyphens only.' });
    // One rule for every surface that authors terms, and the server's (`termsValueProblem`).
    const termsProblem = termsFormProblem(form.terms);
    if (termsProblem) out.push({ field: 'terms', message: termsProblem });
  }

  return out;
}

/**
 * **One phase's problems, by name** — for a surface that authors a phase's fields outside the wizard
 * (the detail page's in-place sections, `3f`). The same rules as the wizard's, so a value the wizard
 * refuses is refused in place, and one it accepts is accepted.
 */
export function phaseProblems(
  phase: StepLabel,
  form: WizardForm,
  existingNames: readonly string[] | null,
): readonly WizardProblem[] {
  return stepProblems(0, form, [phase], existingNames);
}

/**
 * **The names a new or renamed artifact may not take** — the visible list, less its own.
 *
 * An edit that does not rename collides with itself, so a check reading the raw list would refuse
 * every save of every existing source. Found by asserting the rule rather than the fix: the rule is
 * *may this name be taken*, and the subject's own name was never taken **by anything else**.
 *
 * Compared the way the server compares (trimmed, case-insensitive), so the two agree about what a
 * collision is — a console that disagreed would refuse a name the server would have accepted, which
 * is the worse direction.
 */
export function namesInUseBesides(
  existingNames: readonly string[] | null,
  ownName: string | undefined,
): readonly string[] | null {
  if (!existingNames) return null;
  const own = String(ownName ?? '')
    .trim()
    .toLowerCase();
  if (!own) return existingNames;
  return existingNames.filter((name) => name.trim().toLowerCase() !== own);
}

/** A bidder row's parameters, judged on their own — the value's validator, beside the value. */
export function bidderParamsProblem(paramsJson: string): string | null {
  try {
    const parsed = JSON.parse(paramsJson || '{}');
    if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
      return 'parameters must be a JSON object (e.g. {"placementId": 12345}).';
    }
  } catch {
    return 'parameters contain invalid JSON.';
  }
  return null;
}

/**
 * **Why this step cannot be left**, or `null` — the first of {@link stepProblems}.
 *
 * Kept as the phase-level answer because that is what the rail dot, the footer and
 * {@link firstBlockingStep} ask for. What changed with `ST12` item 4 is that it is now **derived**
 * from the per-field list rather than being the only thing there is, so the same judgement reaches
 * the control and the footer and the two cannot disagree.
 */
export function validateStep(
  step: number,
  form: WizardForm,
  steps: readonly StepLabel[],
  existingNames: readonly string[] | null,
): string | null {
  return stepProblems(step, form, steps, existingNames)[0]?.message ?? null;
}

/**
 * **Which acts the footer offers, at the last phase** (`ST12` item 2, `#1358`/`#1359`).
 *
 * The *Save as* choice came off the top of page 4 and became actions here, so what is on the footer
 * is now a decision rather than a layout. Extracted for the reason `clone-options.ts` is: the
 * decision is testable and the rendering is not — this console mounts no wizard in a test.
 *
 * Three states, and each of the other two would be a defect:
 *
 * | | Offers | Why not the others |
 * | --- | --- | --- |
 * | **editing** | `saveAs` · `save` | *what an artifact is* is not something a save re-decides (`TM9`), so no create act — promoting a live source is the clone gesture, which is what `saveAs` opens |
 * | **creating** | `createTemplate` · `create` | both, because the phase's own question is *what am I creating* |
 * | **creating, entry decided** | `create` | the operator came from Templates → New. Offering the choice again is two controls over one slot (`#1229`), and the standing claim is already on the title bar and the labels |
 *
 * The order is the order they render, left to right: the primary act is last, where the eye and the
 * cursor end up.
 */
export type WizardSaveAction = 'saveAs' | 'save' | 'createTemplate' | 'create';

export function footerSaveActions(state: {
  isEditMode: boolean;
  /** The entry point's standing claim — `createAsTemplate`, or the source being edited. */
  isTemplateArtifact: boolean;
}): readonly WizardSaveAction[] {
  if (state.isEditMode) return ['saveAs', 'save'];
  return state.isTemplateArtifact ? ['create'] : ['createTemplate', 'create'];
}

/**
 * **The first step, from `from` up to (not including) `to`, that cannot be passed — and why**, or
 * `null`. One walk for a jump forward and for the final save (`5c-i` rehearsal): the final save used to
 * judge nothing, so page 4's own checks never ran on the button that saves from page 4.
 */
export function firstBlockingStep(
  form: WizardForm,
  steps: readonly StepLabel[],
  from: number,
  to: number,
  existingNames: readonly string[] | null,
): { step: number; error: string; field: WizardFieldKey } | null {
  for (let idx = from; idx < to; idx += 1) {
    const [problem] = stepProblems(idx, form, steps, existingNames);
    // The field travels with the step, so a refusal can reveal the control rather than the phase —
    // `blockingProblemTarget` finds whichever control marked itself invalid, and it can only do that
    // because the problem reached the control in the first place (`ST12` item 4).
    if (problem) return { step: idx, error: problem.message, field: problem.field };
  }
  return null;
}

/**
 * **Where to take the operator for a blocking problem** (`5c-i` rehearsal) — the first control marked
 * invalid inside the step, else the step's error message.
 *
 * Todd: *"The save button should show the user it can't save due to errors — either disabled, or a
 * message on click that takes the user to the error."* The message used to render at the foot of the
 * step's content, below the fold on page 4, so a click that was refused looked like a click that did
 * nothing — *the absence of a response reads as the absence of an effect*. `aria-invalid` is the
 * marker because every control that can hold an invalid value already sets it for screen readers.
 */
export function blockingProblemTarget(container: HTMLElement | null): HTMLElement | null {
  if (!container) return null;
  return (
    container.querySelector<HTMLElement>('[aria-invalid="true"]') ??
    container.querySelector<HTMLElement>('[data-wizard-validation-error]')
  );
}

/** Query-parameter names actually present in an endpoint URL (lower-cased). */
function urlQueryParamNames(url: string): Set<string> {
  try {
    return new Set([...new URL(url.trim()).searchParams.keys()].map((k) => k.toLowerCase()));
  } catch {
    return new Set<string>();
  }
}

// ---------------------------------------------------------------------------
// URL autodetect & helpers
// ---------------------------------------------------------------------------

/** Derive a human-readable name from a URL's hostname as a Quick-Fill fallback. */
function generateNameFromUrl(url: string): string {
  try {
    const hostname = new URL(url).hostname.replace(/^www\./, '');
    const base = hostname.split('.')[0];
    return base.charAt(0).toUpperCase() + base.slice(1);
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Persistent left phase-rail for the full-page shell (#851 D). Vertical, always
 * visible, clickable; each phase carries a worst-severity dot from its editors'
 * validators (guidance §16 surfacing chain: row → editor → phase → footer).
 *
 * One rail, not two (#896 · R1). The 3-versus-5 rails made progress dishonest —
 * "Step 2 of 3" on a source needing parameter work was a false statement, and
 * the mode that produced it had no surface on which to say so. With one rail the
 * dot makes it both a progress indicator and a to-do list.
 */
const StepRail: React.FC<{
  currentStep: number;
  steps: readonly StepLabel[];
  onStepClick: (targetStep: number) => void;
  severityForStep: (label: StepLabel) => MappingIssueLevel | null;
}> = ({ currentStep, steps, onStepClick, severityForStep }) => {
  // Collapsible to an icon strip so the operator can reclaim the ~200px on a
  // narrow window (guidance §17 / design §2). Self-contained UI state.
  const [collapsed, setCollapsed] = React.useState(false);
  return (
    <VStack
      align="stretch"
      gap={1}
      w={collapsed ? '60px' : { base: '150px', md: '210px' }}
      flexShrink={0}
      bg="bg.subtle"
      borderRightWidth="1px"
      borderColor="border.default"
      py={5}
      px={collapsed ? 2 : 3}
      overflowY="auto"
      transition="width 0.15s ease"
    >
      {!collapsed && (
        <Text
          fontSize="10px"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="0.06em"
          color="fg.muted"
          px={2.5}
          mb={2}
        >
          Phases
        </Text>
      )}
      <VStack align="stretch" gap={1} flex="1">
        {steps.map((label, idx) => {
          const isActive = idx === currentStep;
          const isDone = idx < currentStep;
          const severity = severityForStep(label);
          return (
            <Flex
              key={label}
              as="button"
              onClick={() => onStepClick(idx)}
              align="center"
              justify={collapsed ? 'center' : 'flex-start'}
              gap={collapsed ? 0 : 2.5}
              px={collapsed ? 0 : 2.5}
              py={2}
              borderRadius="8px"
              borderLeftWidth={collapsed ? '0' : '2px'}
              borderColor={isActive ? 'accent.solid' : 'transparent'}
              bg={isActive ? 'accent.subtle' : 'transparent'}
              _hover={{ bg: isActive ? 'accent.subtle' : 'bg.canvas' }}
              cursor="pointer"
              textAlign="left"
              title={collapsed ? label : undefined}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`Go to phase ${idx + 1}: ${label}`}
            >
              <Box position="relative" flexShrink={0}>
                <Flex
                  w="22px"
                  h="22px"
                  borderRadius="full"
                  bg={isDone || isActive ? 'accent.solid' : 'border.default'}
                  color={isDone || isActive ? 'white' : 'fg.muted'}
                  align="center"
                  justify="center"
                  fontSize="xs"
                  fontWeight="700"
                >
                  {isDone ? '✓' : idx + 1}
                </Flex>
                {collapsed && severity && (
                  <Box position="absolute" top="-2px" right="-2px">
                    <IssueDot level={severity} title={`${severity} on ${label}`} />
                  </Box>
                )}
              </Box>
              {!collapsed && (
                <>
                  <Box flex="1" minW={0}>
                    <Text
                      fontSize="sm"
                      fontWeight={isActive ? '600' : '400'}
                      color={isActive ? 'accent.solid' : isDone ? 'fg.default' : 'fg.muted'}
                      truncate
                    >
                      {label}
                    </Text>
                    {isActive && (
                      <Text fontSize="10px" color="fg.muted" truncate>
                        {PHASE_QUESTION[label]}
                      </Text>
                    )}
                  </Box>
                  <IssueDot level={severity} title={severity ? `${severity} on ${label}` : undefined} />
                </>
              )}
            </Flex>
          );
        })}
      </VStack>
      <Flex
        as="button"
        onClick={() => setCollapsed((v) => !v)}
        align="center"
        justify={collapsed ? 'center' : 'flex-start'}
        gap={2}
        mt={2}
        px={2.5}
        py={2}
        borderRadius="8px"
        color="fg.muted"
        _hover={{ bg: 'bg.canvas', color: 'fg.default' }}
        cursor="pointer"
        title={collapsed ? 'Expand phases' : 'Collapse phases'}
        aria-label={collapsed ? 'Expand phase rail' : 'Collapse phase rail'}
      >
        <Text fontSize="sm" fontWeight="700">
          {collapsed ? '»' : '«'}
        </Text>
        {!collapsed && <Text fontSize="xs">Collapse</Text>}
      </Flex>
    </VStack>
  );
};

/**
 * The wizard's form field is the shared one (`3h`): the detail page's section editors and the Terms box
 * draw the same field, so a blocking refusal and a non-blocking finding look the same on all three.
 */
const Field = FormField;

// ---------------------------------------------------------------------------
// Step panels
// ---------------------------------------------------------------------------

/**
 * Phase 1 · Connection — "What am I connecting to, and how?"
 *
 * Paste an endpoint or tag, or choose a template; the type falls out of
 * detection and is correctable here. Identity (name, owner, terms) deliberately
 * does **not** live here: those are judgments about a thing whose existence is
 * not yet established, and they move to phase 4 (#896 · R1).
 *
 * **Called `Identify` until `ST11`** (2026-09-22), one letter from the detail page's *Identity*
 * section and naming the opposite thing.
 */
const PhaseConnection: React.FC<{
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
  onQuickFill: (rawUrl: string) => Promise<void>;
  quickFillLoading: boolean;
  quickFillError: string | null;
  quickFillPreset: string | null;
  quickFillSuccess: boolean;
  quickFillWarnings: string[];
  isEditMode: boolean;
  detectedType: DemandSourceMethod | null;
  onUrlChange: (url: string) => void;
  onUrlBlur?: () => void;
  templates: DemandSource[];
  templatesLoading: boolean;
  templatesError: string | null;
  selectedTemplateId: string;
  onSelectTemplate: (presetId: string) => void;
  /**
   * **Why a control on this phase is refused, once the operator has met it** (`ST12` item 4).
   * Required and nullable through the phase's own controls below; the wizard gates it on touched or
   * submitted, so a phase never has to decide when a rule becomes a failure.
   */
  problemFor: (field: WizardFieldKey) => string | undefined;
  onFieldBlur: (field: WizardFieldKey) => void;
  /** The endpoint's non-blocking finding (`#1456`), from `formFieldFindings`. Required and nullable. */
  endpointFinding: FieldFinding | null;
}> = ({
  endpointFinding,
  form,
  onChange,
  onQuickFill,
  quickFillLoading,
  quickFillError,
  quickFillPreset,
  quickFillSuccess,
  quickFillWarnings,
  isEditMode,
  detectedType,
  onUrlChange,
  onUrlBlur,
  templates,
  templatesLoading,
  templatesError,
  selectedTemplateId,
  onSelectTemplate,
  problemFor,
  onFieldBlur,
}) => {
  const [pasteUrl, setPasteUrl] = useState('');
  const [hideTemplateSelector, setHideTemplateSelector] = useState(false);

  React.useEffect(() => {
    if (quickFillSuccess || !!selectedTemplateId) {
      setHideTemplateSelector(true);
    }
  }, [quickFillSuccess, selectedTemplateId]);

  return (
    <VStack gap={5} align="stretch">
      {!isEditMode && (
        <Box bg="accent.subtle" borderRadius="8px" p={3} border="1px solid" borderColor="brand.100">
          <Text fontSize="xs" fontWeight="600" color="accent.solid" mb={2}>
            ⚡ Quick-fill from ad tag URL
          </Text>
          <Text fontSize="xs" color="fg.muted" mb={2}>
            Paste your ad tag URL below — we'll auto-detect the type and pre-fill all fields.
          </Text>
          <Flex gap={2}>
            <Input
              value={pasteUrl}
              onChange={(e) => setPasteUrl(e.target.value)}
              // Enter parses; it does not advance the phase. The wizard's
              // dialog-level Enter handler runs Next on any input, and this box
              // holds its own draft that has not reached `form.endpoint` yet — so
              // Enter after a paste advanced the flow and then rejected it with
              // "URL / Endpoint is required", which is both wrong and the exact
              // opposite of what the operator asked for. Stop propagation so the
              // field's own primary action wins.
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                e.stopPropagation();
                if (!quickFillLoading && pasteUrl.trim()) void onQuickFill(pasteUrl);
              }}
              placeholder="https://ads.example.com/vast?placement=123&cb={cb}"
              size="sm"
              borderColor="brand.100"
              bg="bg.card"
              _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
              flex="1"
            />
            <Button
              size="sm"
              bg="accent.solid"
              color="white"
              _hover={{ bg: 'accent.ink' }}
              onClick={() => onQuickFill(pasteUrl)}
              disabled={quickFillLoading || !pasteUrl.trim()}
              minW="80px"
            >
              {quickFillLoading ? <Spinner size="xs" /> : 'Parse'}
            </Button>
          </Flex>
          {quickFillError && (
            <Text fontSize="xs" color="error.solid" mt={1}>
              {quickFillError}
            </Text>
          )}
          {quickFillPreset && !quickFillError && (
            <Flex align="center" gap={1} mt={1}>
              <Text fontSize="xs" color="success.solid">
                ✓ Recognized as
              </Text>
              <Badge bg="success.solid" color="white" px={1.5} py={0.5} borderRadius="full" fontSize="xs">
                {quickFillPreset}
              </Badge>
              <Text fontSize="xs" color="fg.muted">
                — fields pre-filled below
              </Text>
            </Flex>
          )}
          {!quickFillPreset && !quickFillError && quickFillSuccess && (
            <Text fontSize="xs" color="success.solid" mt={1}>
              ✓ Fields pre-filled — protocol and endpoint below; parameters on the next step
            </Text>
          )}
          {/*
            What the parse DID to the pasted sample (#952). A real ad tag arrives with
            real values in it, and the ones that describe a viewer rather than the
            account are mapped to macros and their literals discarded. That is a
            change to the operator's input, so it is stated rather than assumed.
          */}
          {!quickFillError && quickFillWarnings.length > 0 && (
            <Box mt={2} borderLeftWidth="2px" borderColor="warning.solid" pl={2}>
              {quickFillWarnings.map((warning) => (
                <Text key={warning} fontSize="xs" color="fg.muted" mb={0.5}>
                  {warning}
                </Text>
              ))}
            </Box>
          )}
        </Box>
      )}

      {!isEditMode && !hideTemplateSelector && (
        <Field label="Preset (optional)" hint="Manual preset selection fallback when URL auto-parse is not used.">
          <TemplateSelector
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={onSelectTemplate}
            loading={templatesLoading}
            error={templatesError}
          />
        </Field>
      )}

      {!isEditMode && hideTemplateSelector && (
        <HStack justify="space-between" align="center" p={2} bg="bg.canvas" borderRadius="6px">
          <Text fontSize="xs" color="fg.muted">
            Preset templates hidden after selection/parse.
          </Text>
          <Button size="xs" variant="ghost" color="accent.solid" onClick={() => setHideTemplateSelector(false)}>
            Show templates
          </Button>
        </HStack>
      )}

      <ConnectionEndpointField
        method={form.method}
        value={form.endpoint}
        onChange={onUrlChange}
        onBlur={() => {
          onFieldBlur('endpoint');
          onUrlBlur?.();
        }}
        problem={problemFor('endpoint')}
        finding={endpointFinding}
      />

      {/* Type is an OUTCOME of detection, correctable — not a dropdown reasoned
          about before the endpoint is known (#896 · R1). The full paste
          confirmation panel with per-value provenance is R4 (increment 4);
          this states the claim and offers the correction. */}
      <Field
        label="Type"
        required
        hint={
          detectedType
            ? 'Detected from the endpoint. Change it if the detection is wrong.'
            : 'Paste an endpoint above and this is detected for you.'
        }
      >
        <HStack gap={3} align="center" wrap="wrap">
          <Select.Root
            size="sm"
            collection={typeCollection}
            value={[form.method]}
            // `toMethod`, not a cast: Chakra hands back `string[]`, and a cast is what
            // let an off-union value into the form in the first place.
            onValueChange={(v) => onChange({ method: toMethod(v.value[0]) })}
            width="220px"
          >
            <Select.Trigger borderColor="border.default">
              <Select.ValueText />
            </Select.Trigger>
            <Portal>
              <Select.Positioner>
                <Select.Content>
                  {typeCollection.items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
          {detectedType && detectedType === form.method && (
            <Text fontSize="xs" color="fg.muted">
              ✦ Detected {TYPE_LABELS[detectedType]} from the endpoint. Not right? Change it here.
            </Text>
          )}
          {detectedType && detectedType !== form.method && (
            <Text fontSize="xs" color="warning.ink">
              ✦ The endpoint looks like {TYPE_LABELS[detectedType]}, but this source is set to{' '}
              {TYPE_LABELS[form.method]}.
            </Text>
          )}
        </HStack>
      </Field>
    </VStack>
  );
};

/**
 * **The endpoint control**, shared by the wizard's Connection phase and the detail page's Connection
 * section when it edits in place (`3f`). One control for one field, so the two surfaces cannot come to
 * say different things about what the endpoint is.
 */
export const ConnectionEndpointField: React.FC<{
  method: DemandSourceMethod;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  problem: string | undefined;
  /**
   * A non-blocking finding about the endpoint (`#1456`) — `formFieldFindings(...).endpoint ?? null`.
   * Required and nullable, so a surface that edits the endpoint cannot forget to show one (`#1134`).
   */
  finding: FieldFinding | null;
}> = ({ method, value, onChange, onBlur, problem, finding }) => (
  <Field
    label="Ad Tag URL"
    required
    // Behind the `?` (`3h` rehearsal): the placeholder already shows the shape.
    help={`The endpoint this source calls. Example: ${URL_PLACEHOLDERS[method]}`}
    error={problem}
    finding={finding}
  >
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      // The marker `blockingProblemTarget` looks for, so a refused save can reveal *this* control
      // rather than the phase it is on.
      aria-invalid={problem ? true : undefined}
      aria-label="Ad Tag URL"
      placeholder={URL_PLACEHOLDERS[method]}
      size="sm"
      borderColor="border.default"
      _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
    />
  </Field>
);

/**
 * The protocol half of phase 1 · Connection — the two values that change what goes on
 * the wire without being a mapping: protocol version and timeout. Both are in R2a's
 * demote list, which is why they sit before the proof rather than with the delivery
 * economics in phase 4.
 *
 * **Moved here from the request phase by #995 · R13.** A version is a property of a protocol,
 * and authoring the two a step apart was the clearest instance of the wizard and the
 * detail page describing one source differently. Together they are exactly the detail
 * page's **Connection** section — endpoint, protocol, version, timeout — which is what
 * makes the two surfaces one vocabulary (R11) rather than two.
 */
export const ConnectionSettings: React.FC<{
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
  /** See `PhaseConnection` — the same two, passed through because the phase composes this. */
  problemFor: (field: WizardFieldKey) => string | undefined;
  onFieldBlur: (field: WizardFieldKey) => void;
  /** The timeout's non-blocking finding (`#1456`), from `formFieldFindings`. Required and nullable. */
  timeoutFinding: FieldFinding | null;
}> = ({ form, onChange, problemFor, onFieldBlur, timeoutFinding }) => (
  <VStack gap={5} align="stretch">
    {PROTOCOL_VERSIONS[form.method].hasSelector && (
      <Field
        label="Protocol Version"
        help={
          form.method === 'ORTB'
            ? 'Sets the x-openrtb-version header, and the field names and values the body uses. Match your exchange — placement is sent as plcmt on 2.6 and placement on 2.5, over different value lists.'
            : 'The VAST version this source speaks. Match your ad server; the driver records it for response handling and version-aware behavior.'
        }
      >
        <HStack gap={3} wrap="wrap">
          {PROTOCOL_VERSIONS[form.method].options.map((ver) => {
            const selected = form.protocolVersion === ver;
            return (
              <Button
                key={ver}
                size="sm"
                variant={selected ? 'solid' : 'outline'}
                bg={selected ? 'accent.solid' : 'transparent'}
                color={selected ? 'white' : 'fg.muted'}
                borderColor={selected ? 'transparent' : 'border.default'}
                _hover={{ bg: selected ? 'accent.ink' : 'bg.subtle', color: selected ? 'white' : 'fg.default' }}
                onClick={() => onChange({ protocolVersion: ver })}
              >
                {TYPE_LABELS[form.method]} {ver}
              </Button>
            );
          })}
        </HStack>
      </Field>
    )}

    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={4} alignItems="start">
      <Field
        label="Timeout (ms)"
        help="Range: 500–30000. Leave it empty to use the driver's default. Decides whether the call completes at all, so changing it un-proves the source."
        error={problemFor('timeout')}
        finding={timeoutFinding}
      >
        <NumberField
          minDecimals={null}
          value={form.timeout}
          onChange={(timeout) => onChange({ timeout })}
          onBlur={() => onFieldBlur('timeout')}
          aria-invalid={problemFor('timeout') ? true : undefined}
          size="sm"
          min={500}
          max={30000}
          borderColor="border.default"
          _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
          w="100%"
        />
      </Field>

      {/* Pod support sits with Timeout because both change the request and therefore expire
          a proof (R13). It is a **declaration**, not a detection: a partner's pod support is
          negotiated per integration and is not discoverable from the endpoint (#959), so
          nothing here may set it from a test result. What a test *can* do is prompt — "sent
          6 imps, got 1 bid" is the observation that sends an operator to this control. */}
      <Field
        label="Pod support"
        help="Leave as assumed unless the partner has told you. Assumed means we ask for the whole pod — asking a single-slot partner for more costs a request they ignore, while asking a podding partner for one silently costs fill."
      >
        <HStack gap={2} align="start">
          <NativeSelect.Root size="sm" w="100%">
            <NativeSelect.Field
              value={form.podMode ?? ''}
              onChange={(e) => {
                const value = e.currentTarget.value;
                onChange({
                  podMode: value === '' ? undefined : (value as 'pod' | 'single'),
                  ...(value === 'single' ? { podMaxImpressions: undefined } : {}),
                });
              }}
              borderColor="border.default"
            >
              <option value="">Pod (assumed)</option>
              <option value="pod">Pod — confirmed</option>
              <option value="single">Single ad per request</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {form.podMode === 'pod' && (
            <NumberField
              minDecimals={null}
              value={form.podMaxImpressions}
              onChange={(podMaxImpressions) => onChange({ podMaxImpressions })}
              size="sm"
              min={1}
              placeholder="No limit"
              borderColor="border.default"
              _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
              w="140px"
            />
          )}
        </HStack>
      </Field>
    </Grid>
  </VStack>
);

/** Prebid Server configuration panel (shown inside Request Decoration for OpenRTB sources). */
export const PrebidConfigSection: React.FC<{
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
  /** Nothing to run (`#1456`), from `formFieldFindings`. Required and nullable. */
  finding: FieldFinding | null;
}> = ({ form, onChange, finding }) => {
  const inputStyle = {
    size: 'sm' as const,
    borderColor: 'border.default',
    _focus: { borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' },
    fontFamily: 'mono',
  };

  const addBidderRow = () => {
    onChange({
      prebidBidderRows: [...form.prebidBidderRows, { id: `bidder-${Date.now()}`, name: '', paramsJson: '{}' }],
    });
  };

  const removeBidderRow = (id: string) => {
    onChange({
      prebidBidderRows: form.prebidBidderRows.filter((r) => r.id !== id),
    });
  };

  const updateBidderRow = (id: string, patch: Partial<PrebidBidderRow>) => {
    onChange({
      prebidBidderRows: form.prebidBidderRows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });
  };

  return (
    <Field
      finding={finding}
      label="Prebid Server Configuration"
      hint="Configure this OpenRTB demand source to target a Prebid Server instance. Bidder params are injected into imp[].ext.<bidderName>; stored-request IDs are written to ext.prebid.storedrequest.id."
    >
      <Box border="1px solid" borderColor="border.default" borderRadius="8px" overflow="hidden">
        {/* Enable / disable toggle */}
        <Flex px={3} py={2} bg="bg.subtle" align="center" justify="space-between">
          <HStack gap={2}>
            <Switch.Root
              checked={form.prebidEnabled}
              onCheckedChange={({ checked }) => onChange({ prebidEnabled: checked })}
            >
              <Switch.HiddenInput />
              <Switch.Control bg={form.prebidEnabled ? 'accent.solid' : 'neutral.gray'}>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
            <Text fontSize="xs" fontWeight="600" color="fg.default">
              Prebid Server
            </Text>
          </HStack>
          {form.prebidEnabled && (
            <Text fontSize="xs" color="accent.solid" fontWeight="500">
              Active
            </Text>
          )}
        </Flex>

        {form.prebidEnabled && (
          <Box p={3} bg="bg.subtle">
            {/* Stored-request IDs */}
            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={3} mb={4}>
              <Box>
                <Text fontSize="xs" color="fg.muted" mb={1}>
                  Auction-level Stored Request ID
                </Text>
                <Input
                  {...inputStyle}
                  value={form.prebidStoredRequestId}
                  onChange={(e) => onChange({ prebidStoredRequestId: e.target.value })}
                  placeholder="e.g. my-auction-stored-request"
                />
                <Text fontSize="10px" color="fg.muted" mt={0.5}>
                  Written to ext.prebid.storedrequest.id on the outbound bid request.
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="fg.muted" mb={1}>
                  Impression-level Stored Request ID
                </Text>
                <Input
                  {...inputStyle}
                  value={form.prebidStoredImpId}
                  onChange={(e) => onChange({ prebidStoredImpId: e.target.value })}
                  placeholder="e.g. my-imp-stored-request"
                />
                <Text fontSize="10px" color="fg.muted" mt={0.5}>
                  Written to imp[].ext.prebid.storedrequest.id on every impression.
                </Text>
              </Box>
            </Grid>

            {/* Bidder adapters */}
            <Box>
              <Flex align="center" justify="space-between" mb={2}>
                <Text fontSize="xs" fontWeight="600" color="fg.muted">
                  Bidder Adapters
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  borderColor="accent.solid"
                  color="accent.solid"
                  _hover={{ bg: 'accent.subtle' }}
                  onClick={addBidderRow}
                >
                  + Add Bidder
                </Button>
              </Flex>
              <Text fontSize="10px" color="fg.muted" mb={2}>
                Each bidder's parameters are forwarded verbatim into imp[].ext.&lt;bidderName&gt;. At least one bidder
                or a stored-request ID is required.
              </Text>

              {form.prebidBidderRows.length === 0 ? (
                <Text fontSize="xs" color="fg.muted" fontStyle="italic">
                  No bidders configured — add a bidder or use a stored-request ID above.
                </Text>
              ) : (
                <VStack gap={2} align="stretch">
                  {form.prebidBidderRows.map((row) => (
                    <Box
                      key={row.id}
                      border="1px solid"
                      borderColor="border.default"
                      borderRadius="6px"
                      p={2}
                      bg="bg.card"
                    >
                      <Grid templateColumns={{ base: '1fr', sm: '180px 1fr auto' }} gap={2} alignItems="start">
                        <Box>
                          <Text fontSize="xs" color="fg.muted" mb={1}>
                            Bidder Name
                          </Text>
                          <Input
                            {...inputStyle}
                            value={row.name}
                            onChange={(e) => updateBidderRow(row.id, { name: e.target.value })}
                            placeholder="e.g. appnexus"
                          />
                        </Box>
                        {/* **The value's own validator, beside the value** (`ST12` item 4). This
                            blocked the phase with a sentence at the footer naming the bidder, which
                            is the right information in the wrong place when the box holding the bad
                            JSON is on screen. Judged live rather than on blur because a parse is what
                            the operator is watching for while they type, and it stops the moment it
                            is fixed. */}
                        <Box>
                          <Text fontSize="xs" color="fg.muted" mb={1}>
                            Parameters (JSON)
                          </Text>
                          <Textarea
                            size="sm"
                            fontFamily="mono"
                            fontSize="xs"
                            borderColor={
                              row.name.trim() && bidderParamsProblem(row.paramsJson) ? 'error.border' : 'border.default'
                            }
                            aria-invalid={row.name.trim() && bidderParamsProblem(row.paramsJson) ? true : undefined}
                            _focus={{
                              borderColor: 'accent.solid',
                              boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)',
                            }}
                            minH="60px"
                            value={row.paramsJson}
                            onChange={(e) => updateBidderRow(row.id, { paramsJson: e.target.value })}
                            placeholder='{"placementId": 12345}'
                          />
                          {row.name.trim() && bidderParamsProblem(row.paramsJson) && (
                            <Text fontSize="xs" color="error.ink" mt={1} role="alert">
                              These {bidderParamsProblem(row.paramsJson)}
                            </Text>
                          )}
                        </Box>
                        <Box pt={5}>
                          <Button
                            size="xs"
                            variant="ghost"
                            color="error.solid"
                            _hover={{ bg: 'error.tint' }}
                            onClick={() => removeBidderRow(row.id)}
                            aria-label={`Remove bidder ${row.name}`}
                          >
                            ✕
                          </Button>
                        </Box>
                      </Grid>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Field>
  );
};

/**
 * Request-shaping controls for phase 2 · Connect (#896 · R1).
 *
 * Every section header **states an outcome, including an explicit no-op**.
 * Overload is answered by collapse plus a claim, not by hiding the controls —
 * which is what the mode switch did, and why a user on the default path could
 * not be told what was missing. A section with nothing to do says so; silence
 * reads as an unfinished form, a stated no-op does not.
 *
 * Severity decides what opens: a section with a warning or error is expanded on
 * arrival, everything else stays collapsed behind its claim.
 */
/**
 * **Revert, from inside the wizard** (`#1423`, Todd 2026-09-23: *revert is "remove my override of the
 * parent", and it should be true everywhere*).
 *
 * The wizard is an open draft from the moment it opens, so a revert here is the detail page's editor
 * revert: a server write that takes effect now, after which each editor puts the stored value back
 * into the draft (`useDraftRevert`, `DraftSectionMark`). That reconciliation needs the stored source
 * to move, which is why the wizard reads it live (`useDemandSource`) rather than from the snapshot
 * its caller handed it. `null` when creating: there is nothing stored to revert.
 */
interface WizardRevert {
  onRevert: (paths: string[]) => Promise<unknown>;
  isReverting: boolean;
  parentName: string | undefined;
}

const StepRequestDecoration: React.FC<{
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
  /** Explains parameters dropped when the source was switched to OpenRTB. */
  ortbParamNote?: string | null;
  /**
   * Resolved-request preview (R4a/R4e) — what each row will actually send.
   * Required-nullable to match the editors it forwards to: an intermediate prop that
   * is optional re-opens the same hole one layer up, where it is harder to see.
   */
  preview: RequestPreview | null;
  /**
   * Which rows come from a parent (`P3a`) — threaded rather than resolved here, so this phase and
   * the detail page's in-place editors read the same answer. Nullable, not optional, for the reason
   * `preview` is: an optional prop through a shared component renders nothing and type-checks.
   */
  inheritance: ArtifactInheritance | null;
  /**
   * **Save an expression to the shared library** (`#1134`) — threaded, nullable, for the reason
   * `preview` and `inheritance` above are. This is the surface an operator is most likely to build
   * a reusable expression on, and it was the surface where the control silently did not render.
   */
  onSaveExpressionToLibrary:
    | ((name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>)
    | null;
  /**
   * **Is this wizard building a template?** — threaded rather than re-derived, so the row mark and
   * the save phase agree about what is being created (`describeRequiredMark`).
   *
   * `null` while the answer is genuinely open: the choice is made on **Name and save**, which is
   * the phase that already asks *what am I creating, in what state?* A required row with nothing in
   * it then says only that it is required, which is true on either reading.
   */
  isTemplate: boolean | null;
  /**
   * The rows **as stored**, when this wizard is editing a saved source (`null` when creating).
   *
   * `inheritance` describes the stored artifact and does not move while the form is open, so
   * without this a row the operator has just changed goes on rendering `inherited` — the same
   * defect the detail page had, and the reason this is threaded rather than left optional: an
   * optional prop through a shared component renders nothing and type-checks.
   */
  baseline: { headerRows: HeaderRow[]; parameterRows: ParameterRow[]; bodyLeaves: MappingLeaf[] } | null;
  /** Revert a saved override from a row (`#1423`) — see {@link WizardRevert}. `null` when creating. */
  revert: WizardRevert | null;
  /**
   * **The template this source is a copy of**, for a changed row's way back to it (Todd, 2026-09-24):
   * the one applied in this session, or the saved source's `origin`. `null` where there is none.
   */
  fromTemplate: CopiedFromTemplate | null;
  /**
   * **The applied template's withheld optional fields** (`#1166`) — threaded, nullable, for the
   * reason every prop above it is. `null` says no template is in scope; `[]` says one is and it
   * declares nothing this source lacks, and those are different facts.
   */
  templateOptional: ParameterRow[] | null;
  /** Prebid's non-blocking finding (`#1456`), from `formFieldFindings`. Required and nullable. */
  prebidFinding: FieldFinding | null;
}> = ({
  prebidFinding,
  form,
  onChange,
  ortbParamNote,
  preview,
  inheritance,
  baseline,
  revert,
  fromTemplate,
  onSaveExpressionToLibrary,
  templateOptional,
  isTemplate,
}) => {
  const isOrtb = form.method === 'ORTB';
  // The form is live on every surface, so the index is too (`#944`). And it is **one draft** saved
  // once, so *Apply to all* writes the other surfaces directly: nothing reaches the store before the
  // wizard's own save, which is the review the detail page gets by staging.
  const requestOccurrences = buildRequestOccurrences(
    {
      method: form.method,
      params: form.parameterRows,
      headers: form.headerRows,
      ortbBody: isOrtb ? form.ortbBodyTemplate : null,
    },
    {
      apply: (change) =>
        onChange({
          ...(change.except !== 'params' ? { parameterRows: replaceValueInParams(form.parameterRows, change) } : {}),
          ...(change.except !== 'headers' ? { headerRows: replaceValueInHeaders(form.headerRows, change) } : {}),
          ...(isOrtb && change.except !== 'ortbBody'
            ? { ortbBodyTemplate: replaceValueInBody(form.ortbBodyTemplate, change) }
            : {}),
        }),
      outcome: 'They change in this draft and are saved with the rest of the source.',
    },
  );
  // Named so the required-risk warning can see a default an addressed shared expression declares
  // (`#1254`). This read had no lookup, so a row on such an expression reported *required and
  // derived with no default* — a rail dot raised by correct behaviour.
  const expressionNameFor = useExpressionNameLookup();
  // **What the source's terms do to an authored floor** (`#918`), named with the parent it comes from
  // where it is inherited — the case an operator on this screen cannot see.
  const { lookupParentName } = useArtifactLineage();
  const floorBound = floorBoundOf(
    form.terms.price,
    inheritance,
    inheritance?.parentId ? (lookupParentName(inheritance.parentId) ?? null) : null,
  );

  const headerIssues = worstLevel(validateHeaders(form.headerRows, expressionNameFor));
  const paramIssues = worstLevel(validateParams(form.parameterRows, form.method, floorBound, expressionNameFor));
  const bodyIssues = isOrtb
    ? worstLevel(validateOrtbBody(form.ortbBodyTemplate, expressionNameFor, form.protocolVersion ?? null, floorBound))
    : null;
  const bodySections = Object.keys(form.ortbBodyTemplate?.body ?? {}).length;

  const headerClaim =
    form.headerRows.length === 0
      ? 'Nothing added. The protocol defaults and the session’s own headers are sent.'
      : `${form.headerRows.length} header${form.headerRows.length === 1 ? '' : 's'} sent on top of the protocol defaults.`;

  const paramClaim = isOrtb
    ? form.parameterRows.length === 0
      ? 'Nothing needed. OpenRTB POSTs its endpoint verbatim and carries these in the body.'
      : `${form.parameterRows.length} parameter${form.parameterRows.length === 1 ? '' : 's'} — on an OpenRTB source these land in ext, not the fields they name.`
    : form.parameterRows.length === 0
      ? 'Nothing set. The URL is sent exactly as written above.'
      : `${form.parameterRows.length} parameter${form.parameterRows.length === 1 ? '' : 's'} appended to the URL on every call.`;

  const bodyClaim =
    bodySections === 0
      ? 'Nothing authored. The driver builds the bid request from the session.'
      : form.ortbBodyTemplate?.mode === 'replace'
        ? `${bodySections} section${bodySections === 1 ? '' : 's'} — replaces the driver’s bid request entirely.`
        : `${bodySections} section${bodySections === 1 ? '' : 's'} merged over the driver’s bid request.`;

  return (
    <VStack gap={5} align="stretch">
      <CollapsibleEditorSection
        title="HTTP Headers"
        description={headerClaim}
        count={form.headerRows.length}
        status={headerIssues}
        defaultOpen={headerIssues != null}
      >
        <HeadersEditor
          isTemplate={isTemplate}
          requestOccurrences={requestOccurrences}
          /**
           * **`null` — the wizard's Fix names a step, not a field** (`#1305`). `handleDiagnosisFix`
           * jumps to the phase that owns the problem, and a phase shows one editor, so nothing here
           * holds a row to pass on. Declared rather than omitted, so the surface is saying it.
           */
          openRow={null}
          embedded
          onSaveExpressionToLibrary={onSaveExpressionToLibrary}
          inheritance={inheritance}
          baselineRows={baseline?.headerRows}
          fromTemplate={fromTemplate && { name: fromTemplate.name, rows: fromTemplate.headerRows }}
          onRevert={revert?.onRevert}
          parentName={revert?.parentName}
          isReverting={revert?.isReverting}
          rows={form.headerRows}
          onChange={(headerRows) => onChange({ headerRows })}
          // `null`: the wizard authors no removals, so *Stop overriding* a parent's header is offered on
          // the detail page, as it is for a parameter (`#1126`, `#1422`).
          parentRemovals={null}
          preview={preview}
        />
      </CollapsibleEditorSection>

      <CollapsibleEditorSection
        title="Query Parameters"
        description={paramClaim}
        count={form.parameterRows.length}
        status={paramIssues}
        // On ORTB this is usually a genuine no-op, so it stays shut unless there
        // is something to say (guidance §14). On VAST it is the primary surface.
        defaultOpen={paramIssues != null || (!isOrtb && form.parameterRows.length > 0)}
      >
        {ortbParamNote && (
          <Text fontSize="xs" color="fg.muted" mb={2}>
            {ortbParamNote}
          </Text>
        )}
        <RequestParameterEditor
          /* A source being created has no parent to remove anything from (`#1126`). */
          parentRemovals={null}
          requestOccurrences={requestOccurrences}
          floorBound={floorBound}
          /**
           * **`null` — the wizard's Fix names a step, not a field** (`#1305`). `handleDiagnosisFix`
           * jumps to the phase that owns the problem, and a phase shows one editor, so nothing here
           * holds a row to pass on. Declared rather than omitted, so the surface is saying it.
           */
          openRow={null}
          embedded
          isTemplate={isTemplate}
          onSaveExpressionToLibrary={onSaveExpressionToLibrary}
          inheritance={inheritance}
          baselineRows={baseline?.parameterRows}
          fromTemplate={fromTemplate && { name: fromTemplate.name, rows: fromTemplate.parameterRows }}
          onRevert={revert?.onRevert}
          parentName={revert?.parentName}
          isReverting={revert?.isReverting}
          method={form.method}
          rows={form.parameterRows}
          onChange={(rows) => onChange({ parameterRows: rows })}
          preview={preview}
          templateOptional={templateOptional}
        />
      </CollapsibleEditorSection>

      {/* Free-form OpenRTB request body — the primary body-authoring surface (#858) */}
      {form.method === 'ORTB' && (
        <CollapsibleEditorSection
          title="OpenRTB Request Body"
          description={bodyClaim}
          count={bodySections}
          status={bodyIssues}
          // The primary authoring surface on an ORTB source — open unless the
          // operator has nothing there yet and nothing to fix.
          defaultOpen={bodyIssues != null || bodySections > 0}
        >
          <OrtbBodyEditor
            /* A source being created has no parent to remove anything from (`#1379`). */
            parentRemovals={null}
            /* `null` — a step has no source identity in scope, and the wizard is one visit; the tree opens by rule. */
            rememberKey={null}
            requestOccurrences={requestOccurrences}
            floorBound={floorBound}
            /**
             * **`null` — the wizard's Fix names a step, not a field** (`#1305`). `handleDiagnosisFix`
             * jumps to the phase that owns the problem, and a phase shows one editor, so nothing here
             * holds a row to pass on. Declared rather than omitted, so the surface is saying it.
             */
            openRow={null}
            value={form.ortbBodyTemplate}
            ortbVersion={form.protocolVersion}
            isTemplate={isTemplate}
            onSaveExpressionToLibrary={onSaveExpressionToLibrary}
            onChange={(ortbBodyTemplate) => onChange({ ortbBodyTemplate })}
            preview={preview}
            inheritance={inheritance}
            baselineLeaves={baseline?.bodyLeaves}
            fromTemplate={fromTemplate && { name: fromTemplate.name, leaves: fromTemplate.bodyLeaves }}
            onRevert={revert?.onRevert}
            parentName={revert?.parentName}
            isReverting={revert?.isReverting}
          />
        </CollapsibleEditorSection>
      )}

      {/* Prebid Server configuration — only shown for OpenRTB sources */}
      {form.method === 'ORTB' && <PrebidConfigSection form={form} onChange={onChange} finding={prebidFinding} />}
    </VStack>
  );
};

/** Response Mapping step: inbound extraction controls (VAST/ORTB field mapping + custom pixels). */
const StepResponseMapping: React.FC<{
  form: WizardForm;
  sharedPixels?: ReusablePixel[];
  proxyBaseUrl?: string;
  onChange: (patch: Partial<WizardForm>) => void;
  onSavePixelToLibrary?: (pixel: Partial<ReusablePixel>) => Promise<void>;
  /** "Map from response" (#992), forwarded to `ResponseMappingEditor`. Required-nullable
   *  for the same reason as `preview`: an optional hop hides an unwired leaf. */
  mapFrom: { hasCapture: boolean; run: () => Promise<string> } | null;
  /** The last test's response, shown against the rows (`#1488`), forwarded to `ResponseMappingEditor`. */
  latestResponse: string | null;
  /** What the mapping on screen finds in a sample (`#1144`), forwarded to `ResponseMappingEditor`. */
  previewSample: React.ComponentProps<typeof ResponseMappingEditor>['previewSample'];
  /** Forwarded to `ResponseMappingEditor`; required-nullable for the reason `#1134` records. */
  onSaveExpressionToLibrary:
    | ((name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>)
    | null;
}> = ({
  form,
  sharedPixels,
  proxyBaseUrl,
  onChange,
  onSavePixelToLibrary,
  mapFrom,
  latestResponse,
  previewSample,
  onSaveExpressionToLibrary,
}) => {
  const samplingPct = form.pixelSamplingRate.trim() === '' ? 100 : Number(form.pixelSamplingRate);
  const samplingBelow100 = Number.isFinite(samplingPct) && samplingPct < 100;
  const hasImpressionPixel = form.pixelRows.some((p) => p.type === 'impression');
  const sourceDomain = form.method === 'VAST' ? 'vast-ad-response' : 'ortb-bid-response';
  // Section status dots (guidance §17) — reuse the same validators the editors render.
  const responseStatus = worstLevel(validateResponseMapping(form.responseMapping, form.protocolVersion));
  const pixelStatus = worstLevel(validatePixels(form.pixelRows));

  return (
    <VStack gap={5} align="stretch">
      <Text fontSize="xs" color="fg.muted">
        Define how this source's ad response is parsed and what values are extracted back into the session. After a
        test,{' '}
        <Text as="span" fontWeight="600">
          Ads produced
        </Text>{' '}
        in the evidence above shows what the mapping actually returned — that is the proof it worked, not just that the
        response parsed. For pixel-based extraction, configure capture patterns below.
      </Text>

      {/* **The chips are gone — the panel lives on the mapping surface now** (`#1233`, `XS3` slice
          6). *Already extracted for you* was rendered here, in the wizard, as a line of chips: a
          different rendering of the concept the outbound surfaces show as a collapsible *Sent
          automatically* panel, and absent entirely from the detail page. `ResponseMappingEditor`
          renders it for both, with the `edit` affordance the chips never had. */}

      {/* Ad response field mapping — Universal Mapping Model (#866). */}
      <CollapsibleEditorSection
        title="Ad Response Field Mapping"
        /* **The section owns the intro copy when the editor is embedded**, so it says the same
           sentence rather than its own — `#1187`, and here it is load-bearing rather than tidy: the
           editor's own intro is `!embedded`, so this is the only place the wizard states that a
           mapping runs for each ad in the response (`X56a`, `#1296`). It also said *into the
           session/ad context*, which named a vocabulary not reachable here. */
        description={RESPONSE_INTRO ?? undefined}
        count={form.responseMapping?.entries?.length ?? 0}
        status={responseStatus}
      >
        <ResponseMappingEditor
          /**
           * **`null` — the wizard's Fix names a step, not a field** (`#1305`). `handleDiagnosisFix`
           * jumps to the phase that owns the problem, and a phase shows one editor, so nothing here
           * holds a row to pass on. Declared rather than omitted, so the surface is saying it.
           */
          openRow={null}
          onSaveExpressionToLibrary={onSaveExpressionToLibrary}
          embedded
          value={form.responseMapping}
          sourceDomain={sourceDomain}
          protocolVersion={form.protocolVersion}
          onChange={(responseMapping) => onChange({ responseMapping })}
          mapFrom={mapFrom}
          latestResponse={latestResponse}
          previewSample={previewSample}
        />
      </CollapsibleEditorSection>

      {/* Inbound pixel extractions */}
      <CollapsibleEditorSection
        title="Inbound Pixel Extractions"
        description="Define which pixels to look for and the extraction rules that pull values from fired pixel URLs into the pod/ad object (pixel-type patterns or regex)."
        count={form.pixelRows.length}
        status={pixelStatus}
        defaultOpen={form.pixelRows.length > 0}
      >
        <VStack gap={4} align="stretch">
          <PixelEditor
            /**
             * **`null` — the wizard's Fix names a step, not a field** (`#1305`). `handleDiagnosisFix`
             * jumps to the phase that owns the problem, and a phase shows one editor, so nothing here
             * holds a row to pass on. Declared rather than omitted, so the surface is saying it.
             */
            openRow={null}
            rows={form.pixelRows}
            sharedPixels={sharedPixels}
            proxyBaseUrl={proxyBaseUrl}
            onChange={(pixelRows) => onChange({ pixelRows })}
            onSavePixelToLibrary={onSavePixelToLibrary}
          />

          <Field
            label="Pixel sampling rate"
            hint="Percentage of fired bidBrain /pixel events to record (0–100%). Leave at 100% for impression and billing-critical pixels; lower it only for informational fires (quartile pings, custom analytics not used for billing or impression counting)."
          >
            <HStack gap={2} align="center">
              <Input
                type="number"
                min={0}
                max={100}
                step={1}
                width="120px"
                value={form.pixelSamplingRate}
                placeholder="100"
                onChange={(e) => onChange({ pixelSamplingRate: e.target.value })}
              />
              <Text fontSize="sm" color="fg.muted">
                %
              </Text>
              <Text fontSize="xs" color="fg.muted">
                = 100% (record every event)
              </Text>
            </HStack>
          </Field>

          {samplingBelow100 && (
            <Box
              mt={2}
              px={3}
              py={2}
              borderRadius="md"
              fontSize="xs"
              bg={hasImpressionPixel ? 'error.tint' : 'warning.tint'}
              color={hasImpressionPixel ? 'error.ink' : 'warning.ink'}
            >
              {hasImpressionPixel
                ? 'This source has an impression pixel. Sampling below 100% will under-count impressions — keep it at 100% unless these pixels are purely informational (never used for billing or impression counting).'
                : 'Below 100%, only a sample of pixel fires is recorded. Never sample impression or billing-critical pixels.'}
            </Box>
          )}
        </VStack>
      </CollapsibleEditorSection>
    </VStack>
  );
};

/**
 * The claim phase 4 leads with: the readiness this source will be saved in.
 *
 * **Never rendered from configuration.** Nothing observed means Draft — not
 * Proven, not Healthy. That rule exists to prevent one specific defect, now
 * retired with `deriveHealthState`: a badge that read "Healthy" for anything
 * enabled with a URL, which was a statement about a text field.
 *
 * The labels come from `BADGE_STATES` in the design system via
 * `utils/validation.ts`, so this, the list and the detail header say the same
 * thing about the same source — the seam #897 opened and this closes.
 *
 * Precedence, and why: **a test run in this session wins** over the stored
 * readiness, because it is the more recent observation of the same thing and it
 * is what the server is about to record. Otherwise the stored readiness shows,
 * framed as history ("Currently") rather than as a prediction — whether an edit
 * expires the proof is R2a's call, made server-side on save against the request
 * fingerprint. Guessing at it here would put a second implementation of the
 * demotion rule in the browser, free to disagree with the one that persists.
 */
const ReadinessClaim: React.FC<{
  outcome: TestOutcome | null;
  stored?: DemandSourceValidation;
}> = ({ outcome, stored }) => {
  const storedReadiness = stored && stored.state !== 'unverified' ? stored : undefined;
  // A fresh observation, else the stored one, else nothing observed at all.
  const claim = outcome
    ? {
        label: outcome.summary.label,
        tone: outcome.summary.tone,
        detail: `${outcome.reached.detail}. ${outcome.filled.label}: ${outcome.filled.detail}.`,
      }
    : storedReadiness
      ? { ...validationLabel(storedReadiness), detail: validationDetail(storedReadiness) }
      : null;

  const tone = claim?.tone ?? 'warning';
  const tint = tone === 'success' ? 'success.tint' : tone === 'error' ? 'error.tint' : 'warning.tint';
  const border = tone === 'success' ? 'success.border' : tone === 'error' ? 'error.border' : 'warning.border';
  const ink = tone === 'success' ? 'success.ink' : tone === 'error' ? 'error.ink' : 'warning.ink';

  return (
    <Box
      p={3}
      bg={claim ? tint : 'bg.canvas'}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={claim ? border : 'border.default'}
      borderRadius="6px"
    >
      <Flex align="center" justify="space-between" gap={3} wrap="wrap">
        <Box minW={0}>
          <Text fontSize="xs" color="fg.muted" mb={0.5}>
            {/* A test this session is what will be saved. A stored state is history,
                and an edit may expire it on save — so it is not phrased as a promise. */}
            {outcome ? 'This source will be saved as' : storedReadiness ? 'Currently' : 'This source will be saved as'}
          </Text>
          <Text fontSize="sm" fontWeight="700" color={claim ? ink : 'fg.default'}>
            {claim ? claim.label : 'Draft — never tested'}
          </Text>
          <Text fontSize="xs" color="fg.muted" mt={1}>
            {claim?.detail ?? 'No claim is made about a source nothing has been observed on. Saving is not blocked.'}
          </Text>
          {!outcome && storedReadiness && (
            <Text fontSize="xs" color="fg.muted" mt={1}>
              Changing what this source sends, or how it reads a response, expires the proof — re-test to restore it.
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

/**
 * Phase 4 · Name and save — "What am I creating, in what state?"
 *
 * Identity, ownership and delivery economics, deferred to here deliberately:
 * they are judgments about a thing whose existence is not yet established, and
 * asking for a name before knowing whether the endpoint answers is asking the
 * operator to commit to something they cannot yet evaluate (#896 · R1).
 *
 * It leads with a **readiness claim, not a settings dump**. The old review step
 * listed every configured value, which told the operator what they had typed —
 * something they already knew — and never told them what bidBrain believed about
 * the source they were about to save.
 *
 * Every field on this phase was in R2a's *does not demote* list, and that was not a
 * coincidence: it is why they are here rather than before the proof. **The floor is
 * now the exception** — #1097 put it on the wire, so it demotes (`floor` group) while
 * still belonging beside the contractual terms it is read against (RS5b C1). Its own
 * hint carries that; the phase does not claim it for everything.
 */
const PhaseNameAndSave: React.FC<{
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
  isAdmin: boolean;
  isEditMode: boolean;
  ownerOptions: { value: string; label: string }[];
  /** Outcome of the phase-3 test, when one has been run this session. */
  outcome: TestOutcome | null;
  /** The readiness already recorded for this source, in edit mode (#897). */
  storedReadiness?: DemandSourceValidation;
  /** Which values come from a parent (`P3a`) — see `Field.mark`. */
  inheritance: ArtifactInheritance | null;
  /**
   * **This is a template, and here is what it will carry** (`TM5`, `#1171`).
   *
   * `null` for an ordinary source. On this phase rather than in a confirm dialog because this is the
   * phase that asks *what am I creating, in what state?* — and *everything in it travels to whoever
   * uses it* is part of that answer, at the last moment the operator can still go back and remove
   * something.
   */
  disclosure: readonly TemplateDisclosureLine[] | null;
  /** What a blank term inherits — the tiers beneath this source (`3c-ii`, `#1398`). */
  inheritedTerms: InheritedTerms;
  /**
   * **The source as stored**, read live, so a field mark can tell what the draft changed and put the
   * stored value back (`#1423`). `null` when creating.
   */
  stored: DemandSource | null;
  /** Revert a saved override from a field's mark — see {@link WizardRevert}. `null` when creating. */
  revert: WizardRevert | null;
  /** See `PhaseConnection` — why a control here is refused, once the operator has met it. */
  problemFor: (field: WizardFieldKey) => string | undefined;
  onFieldBlur: (field: WizardFieldKey) => void;
}> = ({
  form,
  onChange,
  isAdmin,
  isEditMode,
  ownerOptions,
  outcome,
  storedReadiness,
  inheritance,
  disclosure,
  inheritedTerms,
  stored,
  revert,
  problemFor,
  onFieldBlur,
}) => {
  /**
   * The shared visibility gesture (`#1124`), so this surface says what a change discloses in the same
   * words as the list and the detail page. In create mode there is nothing stored to describe, so it
   * declines and the change applies straight away.
   */
  const { request: requestVisibility, dialog: visibilityDialog } = useVisibilityChange(
    isEditMode && form.id ? (to) => artifactService.visibilityImpact('demand_source', form.id, to) : null,
    (visibility) => onChange({ visibility }),
  );

  /**
   * The chip for a field that is never inherited, or nothing.
   */
  const fieldMark = (path: string): React.ReactNode => <InheritanceMark inheritance={inheritance} path={path} />;

  /**
   * **A field's mark, stating the draft, with both ways back** (`#1423`).
   *
   * It said *no revert here*, because a revert is a server write and the wizard is an open draft,
   * so the two would disagree. `DraftSectionMark` closes that the way the detail page's editors do:
   * an unsaved change is undone locally, and a saved override is reverted on the server and the
   * field then reset from the reloaded source.
   */
  const draftFieldMark = (path: string, draftValue: unknown, storedValue: unknown, reset: () => void) => (
    <DraftSectionMark
      inheritance={inheritance}
      path={path}
      parentName={revert?.parentName}
      overridesInherited={
        stored !== null &&
        pathInheritance(inheritance, path).state !== 'own' &&
        JSON.stringify(draftValue ?? null) !== JSON.stringify(storedValue ?? null)
      }
      stored={storedValue}
      reset={reset}
      onRevert={revert?.onRevert ?? null}
      isReverting={revert?.isReverting ?? false}
    />
  );
  /** The stored terms, in the form's shape, so a draft member compares like with like. */
  const storedTerms: TermsFormValue | null = stored
    ? termsFormFromStored(stored.terms, stored.restrictions ?? null)
    : null;
  const termMark = (member: TermMember): React.ReactNode =>
    draftFieldMark(
      `terms.${member}`,
      termsToPayload(form.terms)[member],
      storedTerms ? termsToPayload(storedTerms)[member] : undefined,
      () => storedTerms && onChange({ terms: { ...form.terms, [member]: storedTerms[member] } }),
    );

  /**
   * `disclosure` is non-null exactly when a template is being saved — it is *what one would carry*,
   * and an ordinary source has nothing to disclose. Stated once here rather than inferred at each
   * use, so the coupling is a sentence somebody can disagree with rather than a `!== null` repeated
   * three times.
   */
  const isTemplate = disclosure !== null;

  /**
   * Renaming also suggests an id — until the operator has typed one, and never in edit mode where
   * the id is frozen.
   *
   * The return type is annotated deliberately: a literal containing a spread is exempt from
   * excess-property checking, which is how this file's retired `url`/`type` names survived a rename
   * (see `WizardForm`). Annotating keeps the patch fresh at the call site.
   */
  const nameChange = (next: string): Partial<WizardForm> => ({
    name: next,
    ...(form.idTouched || isEditMode ? {} : suggestIdFrom(next)),
  });

  const configured = [
    form.headerRows.length > 0 && `${form.headerRows.length} header${form.headerRows.length === 1 ? '' : 's'}`,
    form.parameterRows.length > 0 &&
      `${form.parameterRows.length} parameter${form.parameterRows.length === 1 ? '' : 's'}`,
    form.method === 'ORTB' &&
      Object.keys(form.ortbBodyTemplate?.body ?? {}).length > 0 &&
      `${Object.keys(form.ortbBodyTemplate?.body ?? {}).length}-section ORTB body`,
    form.method === 'ORTB' && form.prebidEnabled && 'Prebid Server',
    (form.responseMapping?.entries?.length ?? 0) > 0 &&
      `${form.responseMapping?.entries?.length} response mapping${
        form.responseMapping?.entries?.length === 1 ? '' : 's'
      }`,
    form.pixelRows.length > 0 && `${form.pixelRows.length} pixel${form.pixelRows.length === 1 ? '' : 's'}`,
  ].filter(Boolean) as string[];

  return (
    <VStack gap={5} align="stretch">
      {/* **What am I creating? — asked by the footer, not by a radio up here** (`ST12` item 2,
          `#1358`; Todd 2026-09-22, choosing this shape).

          It was a bordered radio pair above the name, and it answered a question the operator asked
          four phases ago — the heaviest thing on the phase, for the rarest decision on it. The
          footer already named the outcome (*Create Template* / *Create Demand Source*), so the radio
          and the button were two controls over one slot (`#1229`), and the one the operator pressed
          was the button.

          It is now **one** control: the footer offers both acts, and *Create Template* shows what
          the template would carry before it writes one — `TM5` at the act rather than on the phase,
          which is where Todd's *"up to them to decide what they want to remove before the save"*
          actually lands. The trade is that a source being authored reads as a source until the press;
          where the **entry point** decided (Templates → New), `disclosure` is non-null and the phase
          says template throughout, as it always did. */}
      {/* The claim this source will be saved with. Never rendered from
          configuration — nothing observed means Draft (R2, design system).

          **Not shown for a template**, and that is `E5` rather than tidiness: `isSelectable`
          excludes a template, so it can never be the artifact that served a request, and a badge
          there would claim an observation it structurally cannot make. `applyValidationOnSave` drops
          the record for the same reason; this is the surface half of one rule. */}
      {disclosure === null ? (
        <ReadinessClaim outcome={outcome} stored={storedReadiness} />
      ) : (
        /* **Say what the template will carry, at save** (`TM5`). Todd, 2026-08-26: *"tell the user:
           every field you save in this template will be included for users of the template… up to
           them to decide what they want to remove before the save."* It replaces a withholding rule,
           and it is better on three counts — the deciding one being that an account-scoped template
           **should** carry the operator's publisher id, so a classifier that stripped it would make
           the common case worse to serve the rare one. */
        <Box borderWidth="1px" borderColor="border.default" borderRadius="md" bg="bg.canvas" p={3}>
          <Text fontSize="sm" fontWeight="600" color="fg.title" mb={1}>
            What this template will carry
          </Text>
          <Text fontSize="xs" color="fg.muted" mb={2}>
            {TEMPLATE_DISCLOSURE_HEADLINE}
          </Text>
          <TemplateDisclosureList
            lines={disclosure}
            emptyNote="Nothing configured yet — this template would start an empty demand source."
          />
        </Box>
      )}

      {/* **The metadata strip — one row** (`3h`, `#1409`; `LP11`): what this is referenced by, what it
          is called, who owns it, who can see it, and whether it runs. Short related fields share a
          row, and each is bounded by what it holds, so a wide screen makes the phase shorter.

          **Id first, and deliberately short** (A7, `#1147`; Todd, 2026-08-26: *"a shorter box implies
          that"*): it is the one that is frozen, so the operator sees what they commit to before the
          label they can change at any time, and a `14rem` box visibly runs out after a good id.
          Suggested from the name until the operator types in it — an id that keeps re-deriving from
          the name is an id that changes, the one thing it must never do.

          It wraps rather than squeezing: on a narrow pane the owner and the two toggles drop to a
          second line, and the name keeps its room. */}
      <Flex gap={4} align="start" wrap="wrap">
        <Box flex="0 0 14rem" minW={0}>
          <Field
            label="ID"
            required
            mark={fieldMark('id')}
            // Permanence stays on the surface and format goes behind the `?` (`ST12` item 5): a format
            // is learned from one failed attempt, permanence is not learnable by trying at all. The
            // *reason* it is frozen went behind the `?` with the format (`#1409`) — it is the same
            // kind of claim, and three clauses under one control is what made the phase long.
            hint={ARTIFACT_ID_FROZEN_HINT}
            help={`${artifactIdPurpose(isTemplate ? 'template' : 'artifact')} ${ARTIFACT_ID_CONSTRAINT_HINT} ${ARTIFACT_ID_FROZEN_HELP}`}
            error={problemFor('id')}
          >
            <Input
              value={form.id}
              onChange={(e) => onChange({ id: e.target.value, idTouched: true })}
              onBlur={() => onFieldBlur('id')}
              aria-invalid={problemFor('id') ? true : undefined}
              placeholder="e.g. springserve-tier-1"
              size="sm"
              disabled={isEditMode}
              bg={isEditMode ? 'bg.canvas' : undefined}
              fontFamily="mono"
              borderColor="border.default"
              _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
            />
          </Field>
        </Box>
        <Box flex="1 1 16rem" minW={0}>
          <Field
            label={isTemplate ? 'Template Name' : 'Source Name'}
            required
            // **Unique** is said here because it is enforced (`#1409`, Todd): the server refuses a
            // duplicate within a kind, and since `ST12` item 3 this surface refuses one it can see.
            // A rule the operator meets at save and nowhere before it is the `#1045` shape.
            help={
              isTemplate
                ? 'A unique, short, descriptive name for this baseline — what an operator picks it by.'
                : 'A unique, short, descriptive name for this demand partner.'
            }
            error={problemFor('name')}
          >
            <Input
              value={form.name}
              onChange={(e) => onChange(nameChange(e.target.value))}
              onBlur={() => onFieldBlur('name')}
              aria-invalid={problemFor('name') ? true : undefined}
              placeholder={isTemplate ? 'e.g. SpringServe — house baseline' : 'e.g. SpringServe — Tier 1'}
              size="sm"
              borderColor="border.default"
              _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
            />
          </Field>
        </Box>
        <Box flex="0 1 13rem" minW={0}>
          {/* **Editing: the stored owner and the one way to change it** (`OT6`, slice `3d`). This select
              wrote `ownerId` into the draft, and since `3d` a save leaves ownership as stored — so it
              was a live control that did nothing (Todd, rehearsing `3d`). A transfer acts on what is
              stored, immediately, with its confirm; it reads `stored`, not the draft. The wizard
              applies visibility on save, so it cannot make the source public first.
              **Creating: the owner of something new**, which is a statement rather than a transfer —
              nobody loses anything — and the server still refuses one a non-admin may not make. */}
          {isEditMode && stored ? (
            <Field label="Owner" help="Changing the owner takes effect when you confirm it, not when you save.">
              <OwnerControl
                presentation="explicit"
                ownerId={stored.ownerId}
                managedBy={stored.managedBy}
                ownerName={stored.ownerName}
                createdBy={stored.createdBy}
                visibility={stored.visibility}
                transfer={{ type: 'demand_source', id: String(stored.id) }}
                canEdit={stored.canEdit !== false}
                makePublic={null}
              />
            </Field>
          ) : (
            <Field
              label="Owner"
              help={
                isAdmin
                  ? 'Admins can assign ownership to any user or to the system.'
                  : 'Owner is assigned automatically from the authenticated user.'
              }
            >
              {isAdmin ? (
                <Select.Root
                  size="sm"
                  collection={createListCollection({ items: ownerOptions })}
                  value={[form.ownerId]}
                  onValueChange={(v) => {
                    const selectedId = v.value[0] ?? '';
                    const selectedLabel = ownerOptions.find((o) => o.value === selectedId)?.label ?? 'System';
                    onChange({ ownerId: selectedId, owner: selectedLabel });
                  }}
                >
                  <Select.Trigger borderColor="border.default">
                    <Select.ValueText placeholder="Select owner…" />
                  </Select.Trigger>
                  <Portal>
                    <Select.Positioner>
                      <Select.Content>
                        {ownerOptions.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Portal>
                </Select.Root>
              ) : (
                <Input
                  value={form.owner || (isEditMode ? 'System' : 'Current User')}
                  size="sm"
                  readOnly
                  borderColor="border.default"
                  bg="bg.canvas"
                />
              )}
            </Field>
          )}
        </Box>
        <Box flex="0 0 auto" minW={0}>
          {/* Authors `visibility`, the canonical field. It wrote the deprecated `isPublic` and resubmitted
              the whole record, which is why the server had to guess which of the two was the edit
              (`#1211`). The change runs through the shared gesture, so this surface says what a change
              discloses in the same words as every other one (`#1124`). */}
          <Field label="Visibility" help="Private sources are only visible to their owner and admins.">
            <HStack gap={3}>
              {(['public', 'private'] as const).map((val) => (
                <Button
                  key={val}
                  size="sm"
                  variant={form.visibility === val ? 'solid' : 'outline'}
                  bg={form.visibility === val ? 'accent.solid' : 'transparent'}
                  color={form.visibility === val ? 'white' : 'fg.muted'}
                  borderColor={form.visibility === val ? 'transparent' : 'border.default'}
                  _hover={{ bg: form.visibility === val ? 'accent.ink' : 'bg.canvas' }}
                  onClick={() => requestVisibility(val)}
                >
                  {val === 'public' ? 'Public' : 'Private'}
                </Button>
              ))}
            </HStack>
            {visibilityDialog}
          </Field>
        </Box>
        <Box flex="0 0 auto" minW={0}>
          <Field
            label={isEditMode ? 'Status' : 'Initial status'}
            help="Enabled sources are included in live ad requests immediately."
          >
            <HStack gap={3}>
              {[true, false].map((val) => (
                <Button
                  key={String(val)}
                  size="sm"
                  variant={form.enabled === val ? 'solid' : 'outline'}
                  bg={form.enabled === val ? (val ? 'success.solid' : 'neutral.gray') : 'transparent'}
                  color={form.enabled === val ? 'white' : 'fg.muted'}
                  borderColor={form.enabled === val ? 'transparent' : 'border.default'}
                  _hover={{ bg: val ? 'success.ink' : 'fg.muted', color: 'white' }}
                  onClick={() => onChange({ enabled: val })}
                >
                  {val ? 'Enabled' : 'Disabled'}
                </Button>
              ))}
            </HStack>
          </Field>
        </Box>
      </Flex>

      <Field
        label="Description"
        mark={draftFieldMark('description', form.description, stored?.description ?? '', () =>
          onChange({ description: stored?.description ?? '' }),
        )}
        help="Optional. Helps your team identify this source at a glance."
      >
        <Textarea
          value={form.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="e.g. Primary VAST supply from SpringServe for CTV campaigns"
          size="sm"
          rows={2}
          borderColor="border.default"
          _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
          resize="none"
        />
      </Field>

      {/* **The one Terms box** (`3c-ii`, `#1398`; `ST2`). Cost, price, weight, required and max rounds
          were two groups under two headings until here — *Terms* and *Delivery economics* — which is
          one concept with two names on one screen. The box is the resolved tier, so a blank member
          shows what it inherits and says from where. None of it demotes Proven (`ST1`). */}
      <Field
        label="Terms"
        mark={draftFieldMark(
          'terms',
          termsToPayload(form.terms),
          storedTerms ? termsToPayload(storedTerms) : undefined,
          // The restrictions sit in the same box and are a separate path, so they stay as drafted.
          () => storedTerms && onChange({ terms: { ...storedTerms, restrictions: form.terms.restrictions } }),
        )}
        help="What this source's agreement says: what we pay it, what it is priced at, how it ranks, whether it is required, and how often it is called."
      >
        <TermsEditor
          value={form.terms}
          onChange={(terms) => onChange({ terms })}
          inherited={inheritedTerms}
          subject="source"
          parentMark={termMark}
        />
      </Field>

      {/* A recap, not a settings dump — one line naming what was configured, so
          the operator can tell an empty source from a configured one without
          re-reading values they just authored. */}
      <Box p={3} bg="bg.canvas" borderRadius="6px" borderWidth="1px" borderStyle="solid" borderColor="border.default">
        <Text fontSize="xs" color="fg.muted">
          <Text as="span" fontWeight="600" color="fg.default">
            {TYPE_LABELS[form.method]}
            {PROTOCOL_VERSIONS[form.method].hasSelector ? ` ${form.protocolVersion}` : ''}
          </Text>{' '}
          · {form.endpoint || 'no endpoint set'}
          {configured.length > 0 ? ` · ${configured.join(' · ')}` : ' · nothing configured beyond the endpoint'}
        </Text>
      </Box>
    </VStack>
  );
};

// ---------------------------------------------------------------------------
// Main wizard component (rendered as an overlay modal)
// ---------------------------------------------------------------------------

export interface SimplifiedDemandSourceWizardProps {
  /**
   * Closed — with the artifact that was created, where one was.
   *
   * The argument exists because **what the wizard saved decides where the operator should land**
   * (`TM1a`): a template belongs on Templates, and that page's list excludes it from the demand
   * sources it was opened from. Passing the artifact rather than a flag keeps the caller reading
   * *what this is* rather than re-deriving it from how the wizard was entered — which stopped being
   * the same question once the choice moved to the last phase.
   */
  onClose: (created?: DemandSource) => void;
  /** When provided, the wizard operates in edit mode for this source. */
  editSource?: DemandSource;
  /**
   * When provided in edit mode, the wizard opens at this phase (0-indexed):
   * `0 = Connection, 1 = Configure Request, 2 = Prove, 3 = Name and save`. Clamped to a
   * valid range automatically. See {@link PHASE_INDEX}.
   */
  initialStep?: number;
  /**
   * Open with this template already applied — the preset's id, which is the template artifact's key
   * (`#1090`).
   *
   * The Templates surface answers *what does this vendor need*, and creating a source from one is
   * the natural next act; making the operator find the same template again in the picker is the
   * gesture this removes. Applied **once**, after the presets load, and only in create mode: it is
   * a starting point, not a link, so anything typed afterwards wins and re-picking in the wizard
   * behaves exactly as it always did.
   */
  initialTemplateId?: string;
  /**
   * **Author a template rather than a demand source** (`TM-C`, `#1171`).
   *
   * Without it the only route to a new template is *create a source → save as template → go back and
   * delete the first one* (Todd, 2026-08-26), which is three acts and a cleanup for one intention.
   *
   * The wizard is the right surface by `TM1a`: a template **is** a demand source (`TM1`), so
   * creating one happens in the type's own creation context. What the Templates page contributes is
   * the *entry* — it is where the operator is when they decide they want one.
   */
  createAsTemplate?: boolean;
  /**
   * **The names already in use, as this viewer can see them** (`ST12` item 3, `#1359`).
   *
   * One rule, three places: `proposeCopyName` avoids a collision, the server refuses one within a
   * kind (`1a`), **and the wizard did neither** — `existingNames` appeared in this file zero times,
   * so the surface an operator meets *first* was the only one that found out at save.
   *
   * A suggestion and a visible-collision check, never a verdict, for the reason
   * `CloneArtifactDialogProps.existingNames` gives: a client holds a visibility-filtered slice of the
   * store, so a console-side uniqueness *ruling* would be confidently wrong about exactly the
   * artifacts it cannot see. What it can honestly say is *you can see one of these already*, which is
   * the common bounce; the server stays the authority for the rest.
   *
   * Required and nullable (`#1134`): `null` is a caller with no list to hand over, which is a
   * statement rather than an omission — and it is what stopped this being passed for as long as it
   * was optional.
   */
  existingNames: readonly string[] | null;
}

export const SimplifiedDemandSourceWizard: React.FC<SimplifiedDemandSourceWizardProps> = ({
  onClose,
  editSource,
  initialStep,
  initialTemplateId,
  createAsTemplate,
  existingNames,
}) => {
  const { user, role } = useAuth();
  const isAdmin = role === 'admin' || role === 'super_admin';
  const isEditMode = !!editSource;
  /**
   * **What is being saved — decided at save, defaulted at entry** (`TM-C`, `#1171`).
   *
   * Corrected 2026-08-26 (Todd): *"when is the template mode determined? I absolutely expect the
   * user to create something they know works before saving it as a template."*
   *
   * The first cut made this a **mode fixed at entry**, which got the rare case right and the common
   * one wrong: the expected flow is *build it, prove it works, then generalise it*, and that
   * decision belongs at the end. Without it the operator had to save the source, leave for the list,
   * promote it there and then delete what they had just saved — the same three-acts-and-a-cleanup
   * complaint, one step removed.
   *
   * So the two are separated, because they are two different facts:
   *
   * | | |
   * | --- | --- |
   * | **`createAsTemplate`** (entry) | *I am building a shape.* Relaxes the two gates that judge whether this is finished enough to test, and is the standing claim below |
   * | **the footer's two acts** | *what gets written.* Chosen on **Name and save**, which is the phase that already asks "what am I creating, in what state?" |
   *
   * An operator who declared nothing at entry passes the gates the ordinary way — they built
   * something that works — and the choice at the end costs them nothing, because there is no longer
   * anything to relax. One who declared a shape gets past the gates and keeps the same choice.
   *
   * **`saveAsTemplate` was state until `ST12` item 2** (`#1358`), set by a radio at the top of the
   * phase and read by the footer's button label. With the choice on the **act**, the only standing
   * claim left is the entry point's, so there is nothing to hold: `handleCreate` is told what it is
   * writing, which is also what stops a `setState` in the same tick as a submit from deciding it.
   */
  const isTemplateArtifact = createAsTemplate === true || editSource?.isTemplate === true;
  /**
   * The names this save may not take — every one the caller can see except this source's own, which
   * an edit that does not rename would otherwise collide with. See {@link namesInUseBesides}.
   */
  const nameCandidates = React.useMemo(
    () => namesInUseBesides(existingNames, editSource?.name),
    [existingNames, editSource?.name],
  );
  // One flow, always (#896 · R1). There is no mode to derive from the source and
  // none to toggle: a mode switch asked the user to predict, before seeing their
  // endpoint's requirements, how much configuration they would need — and the two
  // steps that decide whether a source works lived behind it, so a user on the
  // default path could not be told what was missing.
  const steps = PHASES;
  /**
   * **The source as stored, read live** (`#1423`). Two of the wizard's callers hand it a snapshot
   * (the list's and Templates'), so after a revert its baseline would never move and no editor could
   * put the reverted value back into the draft. The query is the detail page's, keyed by id, so a
   * revert's reload reaches it whichever page opened the wizard. The snapshot stands in while it loads.
   */
  const { data: liveSource } = useDemandSource(editSource?.id ?? '');
  const storedSource: DemandSource | undefined = editSource ? (liveSource ?? editSource) : undefined;
  const [step, setStep] = useState(() => {
    if (!isEditMode || initialStep === undefined) return 0;
    return Math.max(0, Math.min(initialStep, steps.length - 1));
  });
  /**
   * The rows **as stored**, so the inherited/overridden mark can tell a row the operator has just
   * changed from one they have not. Built once from the source the wizard opened — not from `form`,
   * which is the thing being compared against it. `null` when creating: nothing is stored yet and
   * every row is the operator's by construction.
   */
  const storedRows = React.useMemo(
    () =>
      storedSource
        ? {
            headerRows: headersObjectToRows(storedSource.headers, storedSource.headersProvenance),
            parameterRows: paramsDtoToRows(
              storedSource.params ?? (storedSource as DemandSource & { params?: Record<string, unknown> }).params,
            ),
            bodyLeaves: templateToLeaves(storedSource.ortbBodyTemplate),
          }
        : null,
    [storedSource],
  );

  const [form, setForm] = useState<WizardForm>(() =>
    editSource ? formFromDemandSource(editSource) : { ...DEFAULT_FORM },
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  /** The step `validationError` was raised about, so the footer can re-judge it as the form changes. */
  const [validationErrorStep, setValidationErrorStep] = useState<number | null>(null);
  /** Bumped on every refused action, so the same problem refused twice is revealed twice. */
  const [blockedRevealRequest, setBlockedRevealRequest] = useState(0);
  const scrollBodyRef = React.useRef<HTMLDivElement | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [detectedType, setDetectedType] = useState<DemandSourceMethod | null>(null);
  const [ortbParamNote, setOrtbParamNote] = useState<string | null>(null);
  const [quickFillLoading, setQuickFillLoading] = useState(false);
  const [quickFillError, setQuickFillError] = useState<string | null>(null);
  const [quickFillPreset, setQuickFillPreset] = useState<string | null>(null);
  const [quickFillSuccess, setQuickFillSuccess] = useState(false);
  const [quickFillWarnings, setQuickFillWarnings] = useState<string[]>([]);
  const [reviewQuickTestResult, setReviewQuickTestResult] = useState<QuickTestResult | null>(null);
  const [reviewQuickTestError, setReviewQuickTestError] = useState<Error | null>(null);
  const [reviewQuickTestLoading, setReviewQuickTestLoading] = useState(false);
  /**
   * The config the last test actually ran against (#895). Snapshotted rather
   * than read live from `form`, so a diagnosis always describes the request that
   * produced the result — editing the form after a failing test must not silently
   * re-point the diagnosis at a config that was never sent.
   */
  const [reviewQuickTestSource, setReviewQuickTestSource] = useState<Partial<DemandSource> | null>(null);
  /**
   * **Test as** (`#979`) — one choice for everything this wizard runs the rig for: the test, the
   * rows' marks, *Map from response* and the response preview.
   */
  const testContext = useTestContext();
  /**
   * **The template artifacts, not a projection of them** (`TM-A`, `#1169`).
   *
   * This was `AdServerPreset[]` — a four-content-field view of a document with dozens — so an ORTB
   * template's body, response mapping, terms and pixels were dropped between the artifact and this
   * component and applying one produced a source that carried none of them.
   */
  /**
   * **Save an expression to the shared library, from the surface it was built on** (`#1134`).
   *
   * Same handler shape as `PixelsPage` — one library, one gesture. The wizard is where an operator
   * most often builds an expression worth reusing, and the control silently did not render here
   * because the prop was optional and nobody passed it.
   */
  const createExpression = useCreateExpression();
  // The rail dot reads the same validators the editors do, so it needs what they are given
  // (`#1254`) — without the lookup a row on a shared expression that declares a default raises a
  // rail dot for correct behaviour, on the default path through creation.
  const expressionNameFor = useExpressionNameLookup();
  /**
   * **Promoting returns what it created, so the row can address it** (`X30d`, `#1304`).
   *
   * `mutate` was fire-and-forget, which is why *Save as shared* left the row holding a copy of the
   * entry it had just made: the handler could not say what it created, so nothing could be pointed
   * at it. `mutateAsync` returns the entry, and `DecoratorExpressionEditor` replaces the value with
   * a reference to it — on success only, so a failed create never costs the operator the expression.
   */
  const saveExpressionToLibrary = (name: string, description: string, expression: MacroExpression) =>
    createExpression.mutateAsync({ name, description: description || undefined, expression, enabled: true });

  const [templates, setTemplates] = useState<DemandSource[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedPresetId] = useState('');
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState<string | null>(null);
  const [showSavedBadge, setShowSavedBadge] = useState(false);
  const [isFooterElevated, setIsFooterElevated] = useState(false);

  const createMutation = useCreateDemandSource();
  const updateMutation = useGuardedUpdateDemandSource();
  const { data: usersList } = useUsers(isAdmin);
  const { data: sharedPixels } = usePixels();
  const createPixelMutation = useCreatePixel();
  // Save a demand-source pixel to the shared library. The pixels query
  // auto-invalidates on success, so it appears in "Attach Shared Pixel".
  const handleSavePixelToLibrary = React.useCallback(
    async (pixel: Partial<ReusablePixel>) => {
      await createPixelMutation.mutateAsync(pixel);
    },
    [createPixelMutation],
  );
  const { data: systemDefaults } = useSystemDefaults();
  const ownerOptions = React.useMemo(
    () => [
      { value: '', label: 'System' },
      ...(usersList ?? []).map((u) => ({ value: u.id, label: u.name || u.email })),
    ],
    [usersList],
  );
  const lastStep = steps.length - 1;

  /**
   * Worst issue severity for a phase, from the same validators its editors
   * render (guidance §16 surfacing chain). Drives the rail dot, which is what
   * makes one rail both a progress indicator and a to-do list: a source needing
   * parameter work now says so on the default path, which is the thing the
   * mode switch made impossible (#896).
   */
  const severityForStep = React.useCallback(
    (label: StepLabel): MappingIssueLevel | null => {
      if (label === 'Configure Request') {
        return worstLevel([
          ...validateHeaders(form.headerRows, expressionNameFor),
          // Where the floor comes from changes the sentence, never the level — and this is a rail dot, so
          // the bound is read without its parent's name (`wizardInheritance` is loaded further down).
          ...validateParams(
            form.parameterRows,
            form.method,
            floorBoundOf(form.terms.price, null, null),
            expressionNameFor,
          ),
          ...(form.method === 'ORTB'
            ? validateOrtbBody(
                form.ortbBodyTemplate,
                expressionNameFor,
                form.protocolVersion ?? null,
                floorBoundOf(form.terms.price, null, null),
              )
            : []),
        ]);
      }
      if (label === 'Prove & map') {
        return worstLevel([
          ...validateResponseMapping(form.responseMapping, form.protocolVersion),
          ...validatePixels(form.pixelRows),
        ]);
      }
      return null;
    },
    [form, expressionNameFor],
  );

  // Recorded versions are left out: opening a row writes one, and that is not work to lose (`#1302`).
  const currentFormSnapshot = React.useMemo(() => JSON.stringify(form, withoutRecordedVersions), [form]);
  const hasUnsavedChanges = isEditMode && lastSavedSnapshot !== null && currentFormSnapshot !== lastSavedSnapshot;

  /**
   * The form as this wizard opened, for the close guard (#1129).
   *
   * **A different question from `hasUnsavedChanges`, deliberately.** That one asks *have you
   * diverged from what is stored*, which is the chip's question and is meaningless during a create —
   * everything is unsaved until the operator presses Create, and a permanent "Unsaved" through the
   * whole flow is noise. This asks *would closing lose work*, which is true in create mode the
   * moment anything is typed.
   */
  const pristineSnapshot = React.useRef<string | null>(null);
  if (pristineSnapshot.current === null) pristineSnapshot.current = currentFormSnapshot;
  // The decision itself lives in the helpers, where it can be asserted without a DOM — the console
  // has no component-test setup, and the case this was missing (create mode) is invisible in the
  // happy path. See `shouldReveal` for the same split.
  const losesWork = wouldLoseWork({
    isEditMode,
    current: currentFormSnapshot,
    pristine: pristineSnapshot.current,
    lastSaved: lastSavedSnapshot,
  });

  /**
   * **The same answer, offered to the fourth exit** (`#1245`).
   *
   * `#1129` built this for Escape, the ✕ and the backdrop; *a nav away* was in the rule and reached
   * nothing. The wizard already knows whether leaving would lose work, so the nav guard does not
   * compute a second opinion — it asks this one. Two definitions of *dirty* on one screen is what
   * `#1122` measured elsewhere in this console, and it is why this is a registration rather than a
   * reimplementation.
   */
  useUnsavedWork(() => losesWork);

  /**
   * Closing asks first when there is something to lose (#1129).
   *
   * Escape reached `onClose()` directly, and a create has no `hasUnsavedChanges` guard, so **one
   * keystroke discarded a part-built demand source with no confirm** — reachable from an entirely
   * natural gesture, cancelling an inline add. The ✕ has the same reach with a mis-click.
   *
   * Two presses with **feedback between them**, not a silent first press: a wizard that appears to
   * ignore Escape is the failure `useRevealOnOpen` documents — the absence of a response is read as
   * the absence of an effect, and the operator presses harder.
   */
  /**
   * **The two acts the footer offers beside Save** (`ST12` item 2, `#1358`/`#1359`).
   *
   * `saveAsOpen` is the clone gesture reached from an **open editor** — the path that had no route —
   * and `createTemplateConfirm` is `TM5`'s disclosure, shown at the press rather than standing on
   * the phase. Both are dialogs rather than armed state, for `#1129`'s reason: state that arms an
   * irreversible act and is not visibly attached to the thing that armed it is a trap.
   */
  /**
   * **Which controls the operator has met** (`ST12` item 4, `#1359`).
   *
   * A blocking problem reaches its control once the box has been left or a save has been refused —
   * `CloneArtifactDialog`'s `nameTouched || submitted`, generalised to a phase. On a pristine form a
   * required field is a **rule**, and rendering it in red the moment the wizard opens reads as a
   * failure the operator caused.
   */
  const [touchedFields, setTouchedFields] = useState<ReadonlySet<WizardFieldKey>>(() => new Set());
  const markFieldTouched = React.useCallback((field: WizardFieldKey) => {
    setTouchedFields((prev) => (prev.has(field) ? prev : new Set([...prev, field])));
  }, []);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  /**
   * **The name the template will take**, proposed when the confirm opens (`ST12` item 3, ruled by
   * Todd 2026-09-22 — *creating a template adds `Template` to the end of the name*).
   *
   * `null` is *the confirm is closed*, so opening it always re-proposes from the name as it stands
   * rather than from whatever it was the last time. The operator typed a name for a **source**; this
   * is the one act on this phase that changes what is being made, so it is the one place the
   * direction rule has anything to say.
   *
   * Editable, because a proposal that cannot be accepted is not a default and one that cannot be
   * changed is not a proposal.
   */
  const [createTemplateName, setCreateTemplateName] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const cloneMutation = useCloneDemandSource();
  const [closeConfirm, setCloseConfirm] = useState(false);
  const requestClose = React.useCallback(() => {
    if (!shouldConfirmClose(losesWork, closeConfirm)) {
      onClose();
      return;
    }
    setCloseConfirm(true);
  }, [losesWork, closeConfirm, onClose]);

  /**
   * The dialog owns the answer, so there is no armed state to survive a navigation (#1129).
   *
   * The first attempt set a flag on one Escape and closed on the next, with the question in the
   * header — and the flag outlived anything that was not a keystroke: *"I make a change, hit esc.
   * Then go to a new page, hit esc again, lose data."* The operator's second Escape was, to them, a
   * first Escape on a different screen. **The fix is not better clearing rules; it is not having the
   * state.**
   */

  /**
   * What the title bar says **before the operator has named anything** (#1115).
   *
   * It states the protocol as soon as one is chosen, because that is the decision everything after
   * it depends on and it is made on a screen the operator has already left. Until then it is the
   * bare type — never a guess at which protocol they are heading for.
   *
   * No verb in it: the header's eyebrow already says `New` or `Editing`, and "New ORTB Demand
   * Source" beside a label reading NEW says one thing twice.
   */
  /**
   * What the title bar calls the thing being authored.
   *
   * A template says so, because for the first two phases there is no name and the operator is
   * looking at an endpoint full of `{placeholders}` that would read as a half-finished source. It is
   * `TM6`'s *a template is not an instance* arriving in the one place that says what this is.
   */
  const wizardTypeLabel = isTemplateArtifact
    ? `${form.method ?? ''} Demand Source Template`.trim()
    : form.method
      ? `${form.method} Demand Source`
      : 'Demand Source';

  /**
   * The preset applied **in this session**, from either route into one (the picker or Quick-fill).
   *
   * Session-scoped and deliberately so: nothing on the saved artifact records that a preset was
   * used, which is exactly what #1115 is about. Claiming it after a reload would be inventing a
   * relationship the data does not hold — so the chip appears while it is true and disappears when
   * we genuinely no longer know, rather than persisting a guess.
   */
  /**
   * The template applied in this session, resolved once so its name and its documentation cannot
   * come from different places (`#1115`).
   *
   * Two routes reach one: the picker sets `selectedTemplateId`, Quick-fill reports the detected
   * preset by **name**. Looking the second one up rather than carrying a second half-record is what
   * keeps the chip's label and its link describing the same template.
   */
  const appliedTemplate =
    (selectedTemplateId ? templates.find((t) => t.id === selectedTemplateId) : undefined) ??
    (quickFillPreset ? templates.find((t) => t.name === quickFillPreset) : undefined);
  const appliedTemplateName = appliedTemplate?.name ?? quickFillPreset ?? undefined;
  /**
   * **What the applied template declares and did not seed** (`#1166`) — the parameter editor's
   * `+ Add optional` and its picker entries.
   *
   * `null` rather than `[]` when no template is applied, because the two say different things: *no
   * template is in scope* is why the affordance is absent, and *this template declares nothing you
   * are missing* is why it is empty. Resolved from `appliedTemplate` so it cannot describe a
   * different template from the chip beside it.
   */
  const templateOptional = React.useMemo(
    () => (appliedTemplate ? templateSuppliedRows(appliedTemplate) : null),
    [appliedTemplate],
  );
  const appliedTemplateUrl = appliedTemplate?.documentationUrl;
  /** What a changed row reverts to — the template applied here, or the saved source's (Todd, 2026-09-24). */
  const copiedFrom = useCopiedFromTemplate(appliedTemplate?.objectId ?? storedSource?.origin);

  // Identity beside the name needs the same lineage lookup every other surface uses; a wizard that
  // resolved parents its own way is the drift `useArtifactLineage` exists to prevent.
  const { lookupParentName, parentIsTemplate } = useArtifactLineage();

  /**
   * Which rows come from a parent, for the editors this wizard embeds (`P3a`).
   *
   * The wizard edits the **same rows** the detail page does, through the same components, so it has
   * to resolve them the same way or the two surfaces disagree about which values are the operator's
   * — the drift one projection exists to prevent, reintroduced through a missing argument. Empty in
   * create mode, which is correct: there is no artifact yet to have a parent.
   */
  const { data: wizardInheritance } = useArtifactInheritance(DEMAND_SOURCE_INHERITANCE, editSource?.id ?? '');
  const revertMutation = useRevertArtifactValues(DEMAND_SOURCE_INHERITANCE);
  const wizardRevert: WizardRevert | null = editSource
    ? {
        onRevert: (paths) => revertMutation.mutateAsync({ id: editSource.id, paths }),
        isReverting: revertMutation.isPending,
        parentName: wizardInheritance?.parentId ? lookupParentName(wizardInheritance.parentId) : undefined,
      }
    : null;
  /**
   * **What a blank term inherits** (`3c-ii`, `#1398`). A saved source carries its resolution, which
   * names every tier beneath it, partner included. A new one has none, and names no partner until the
   * picker lands (`#837`), so Standard Terms is the whole of what is beneath it.
   */
  const { data: standardTerms } = useStandardTerms();
  const inheritedTerms: InheritedTerms = storedSource?.resolvedTerms
    ? inheritedFromResolved(storedSource.resolvedTerms)
    : inheritedFromStandardTerms(standardTerms);

  React.useEffect(() => {
    if (!isEditMode || lastSavedSnapshot !== null) return;
    setLastSavedSnapshot(currentFormSnapshot);
  }, [currentFormSnapshot, isEditMode, lastSavedSnapshot]);

  const onChange = (patch: Partial<WizardForm>) => {
    // Switching source type resets the protocol version to the new type's default —
    // VAST versions (4.x/3.0/2.0) and OpenRTB versions (2.5/2.6) are disjoint, so a
    // carried-over value would be nonsense on the wire. The caller can still override
    // in the same patch (e.g. loading a preset).
    if (patch.method && patch.method !== form.method && patch.protocolVersion === undefined) {
      patch = { ...patch, protocolVersion: PROTOCOL_VERSIONS[patch.method].default };
    }
    // Switching to OpenRTB: drop parameters that were only *suggested* for a VAST tag,
    // and keep the ones genuinely present in the endpoint URL — some SSPs do require one
    // (e.g. a Prebid `account_id`).
    //
    // The reason changed on 2026-08-17 and the behaviour did not. It used to be that an
    // OpenRTB source POSTed its endpoint verbatim, so a suggested `ip` would silently end
    // up in `bidRequest.ext.*` instead of the field it names. Since D18 a row *is* a query
    // parameter on both protocols and also fills the body where the concept has a home
    // there — so nothing lands anywhere surprising any more. It is still right to drop
    // them: an OpenRTB exchange reads device, user and consent from the body, so a
    // suggested `?ip=` is noise the operator did not ask for. Keeping a stale mechanism as
    // the stated reason is the #1045 defect, hence this note rather than a silent edit.
    if (patch.method === 'ORTB' && form.method !== 'ORTB') {
      const inUrl = urlQueryParamNames(patch.endpoint ?? form.endpoint);
      const kept = form.parameterRows.filter((row) => inUrl.has(row.name.trim().toLowerCase()));
      const dropped = form.parameterRows.length - kept.length;
      setOrtbParamNote(
        dropped > 0
          ? `Removed ${dropped} suggested query parameter${dropped === 1 ? '' : 's'} — OpenRTB carries device, user and consent values in the request body.`
          : null,
      );
      setForm((prev) => ({
        ...prev,
        ...patch,
        parameterRows: kept,
        ortbBodyTemplate: seedOrtbBodyTemplate(prev.ortbBodyTemplate, patch.protocolVersion ?? prev.protocolVersion),
      }));
      setValidationError(null);
      return;
    }
    if (patch.method && patch.method !== 'ORTB') setOrtbParamNote(null);
    setForm((prev) => ({ ...prev, ...patch }));
    setValidationError(null);
  };

  React.useEffect(() => {
    let active = true;
    const run = async () => {
      setTemplatesLoading(true);
      setTemplatesError(null);
      try {
        const next = await demandSourceService.listTemplates();
        if (!active) return;
        setTemplates(next);
      } catch (error: unknown) {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Failed to load templates.';
        setTemplatesError(message);
      } finally {
        if (active) {
          setTemplatesLoading(false);
        }
      }
    };
    run();
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (isEditMode) return;

    const ownerName = user?.name || user?.email || 'Current User';
    setForm((prev) => ({
      ...prev,
      owner: ownerName,
      ownerId: user?.id ?? '',
      visibility: 'private',
    }));
  }, [isEditMode, user]);

  /** URL field handler: updates URL and auto-detects type from the pasted/typed value. */
  const handleUrlChange = (url: string) => {
    const detected = detectTypeFromUrl(url);
    setDetectedType(detected);
    setForm((prev) => ({
      ...prev,
      endpoint: url,
      // `method`, not `type`. This wrote `type` — not a field on `WizardForm` — and a
      // *conditional spread* is exempt from excess-property checking, so protocol
      // auto-detection from a pasted endpoint silently stopped working at #936 while
      // still compiling.
      ...(detected && detected !== prev.method
        ? { method: detected, protocolVersion: PROTOCOL_VERSIONS[detected].default }
        : detected
          ? { method: detected }
          : {}),
    }));
    setValidationError(null);
  };

  /**
   * On blur, not on change (#874 · D18): an endpoint carrying `?a=1` has its parameters
   * lifted into the Query Parameters editor and the URL left as the address we call.
   *
   * Blur rather than keystroke because splitting mid-type fights the person typing — they
   * would lose the `?` the moment they wrote it. And it is announced, because a field that
   * silently rewrites what someone pasted is the *"why'd you change it?"* complaint this
   * behaviour exists to answer. The parameters still go out: the driver appends exactly
   * these rows back onto the endpoint.
   */
  const handleUrlBlur = () => {
    const lifted = liftEndpointQuery(form.endpoint, form.parameterRows);
    if (!lifted) return;
    setForm((prev) => ({ ...prev, endpoint: lifted.endpoint, parameterRows: lifted.parameterRows }));
    setOrtbParamNote(lifted.note);
  };

  /**
   * The rows a template seeds — extracted so a **switch** can tell its own rows from the operator's
   * (`#1115`).
   *
   * The guard it replaces was `prev.parameterRows.length > 0 ? prev.parameterRows : seeded`, whose
   * intent was right — never destroy what the operator typed — and whose reach was wrong: it could
   * not tell a seeded row from an authored one, so the **first** template chosen seeded rows and
   * every later one seeded nothing. Google's `iu` survived a switch to FreeWheel, where it means
   * nothing.
   *
   * Recomputing what the *previous* template seeded is what makes the distinction possible, and it
   * is the same test `isUneditedTemplateEndpoint` already applies one field over: a value that still
   * matches what a template produced is by definition one we put there.
   */
  /**
   * **Apply a template — the whole document, minus the artifact's identity** (`TM-A`, `#1169`).
   *
   * What stood here read `AdServerPreset.fields` and rebuilt parameter rows out of the vendor's
   * `DestinationField` declarations — a *third* translation of information the template artifact
   * already holds as rows, and the only destination that translation had. An ORTB template's body
   * had no route through it at all, so applying `magnite-ortb` produced a source with 55 leaves
   * missing and a protocol-generic starter body in their place.
   *
   * The document is now the seam: {@link applyTemplateToForm} hydrates it and decides, per plane,
   * what a new artifact may keep. Nothing here enumerates a field.
   */
  const applyTemplate = (templateId: string) => {
    const template = templates.find((item) => item.id === templateId);
    if (!template) return;

    /**
     * **The template the operator is leaving** — the set a switch may withdraw (`#1115`).
     *
     * Recomputed from the artifact rather than remembered: a stored "these were seeded" list would
     * be a second account of the form's values, free to disagree with them the moment anything else
     * edited one.
     */
    const previousTemplate = selectedTemplateId ? templates.find((item) => item.id === selectedTemplateId) : undefined;

    setSelectedPresetId(templateId);
    setForm((prev) => applyTemplateToForm(prev, template, previousTemplate, templates, nameCandidates));
  };

  /**
   * Apply the template the operator arrived with (`#1090`).
   *
   * **Once, and only in create mode.** `appliedInitialTemplate` is a ref rather than a dependency
   * guard because `applyTemplate` writes the form: re-running it would overwrite whatever they had
   * typed since, which is the *arming a flag* failure #1129 already paid for in another form —
   * state that acts on the operator's work after they have moved on.
   *
   * Waits for the templates to load rather than fetching its own: one vocabulary, one request, and a
   * deep link to a template that no longer exists then simply does nothing rather than erroring at
   * a moment the operator cannot act on it.
   */
  const appliedInitialTemplate = React.useRef(false);
  React.useEffect(() => {
    if (appliedInitialTemplate.current) return;
    if (isEditMode || !initialTemplateId || templates.length === 0) return;
    if (!templates.some((template) => template.id === initialTemplateId)) return;
    appliedInitialTemplate.current = true;
    applyTemplate(initialTemplateId);
    // `applyTemplate` is deliberately not a dependency: it is redefined every render, and the ref is
    // what decides when this runs. Listing it would re-arm nothing while implying it might.
  }, [templates, initialTemplateId, isEditMode]);

  /**
   * Quick-fill handler: calls the backend parse-ad-tag API and pre-fills all wizard
   * fields (name, type, url, timeout, description) from the parsed result.
   */
  const handleQuickFill = async (rawUrl: string) => {
    const trimmed = rawUrl.trim();
    if (!trimmed) return;
    setQuickFillLoading(true);
    setQuickFillError(null);
    setQuickFillPreset(null);
    setQuickFillSuccess(false);
    setQuickFillWarnings([]);
    try {
      // Hint the parser with the type the operator already chose (or the one the
      // URL betrays) so it doesn't suggest VAST URL parameters for an OpenRTB
      // source — those belong in the request body, not the query string.
      const typeHint = form.method !== 'VAST' ? form.method : (detectTypeFromUrl(trimmed) ?? undefined);
      const result = await demandSourceService.parseAdTag(trimmed, typeHint);
      const ds = result.demandSource;
      const inferredType: DemandSourceMethod = typeHint ?? toMethod(ds.method);
      // **A proposal the operator can accept** (`ST12` item 3): suffixed where it collides with one
      // they can see, rather than left to be refused at save. The parsed tag's own name is a
      // proposal exactly as the derived one is, so both go through it.
      const parsedName = disambiguateName(ds.name ?? generateNameFromUrl(trimmed), nameCandidates ?? []);
      const parsedRows =
        Array.isArray(result.detectedParameters) && result.detectedParameters.length > 0
          ? buildRowsFromDetectedParameters(result.detectedParameters)
          : paramsDtoToRows(
              (ds.params as Record<string, unknown> | undefined) ??
                (ds.queryParams as Record<string, unknown> | undefined) ??
                (ds.fields ? { fields: ds.fields } : undefined),
            );
      setForm((prev) => ({
        ...prev,
        name: parsedName || prev.name,
        method: inferredType,
        // A pasted tag does not declare a protocol version (the parser never sets one), so the
        // type's default — the same answer the old read of an always-empty bag gave.
        protocolVersion: PROTOCOL_VERSIONS[inferredType].default,
        // `endpoint`, the form's field. This read `url:` — a key `WizardForm` no longer
        // has — and the trailing conditional spread below exempts the whole literal
        // from excess-property checking, so Quick-fill silently stopped filling the
        // endpoint at #936. (`ds.url` is the *parse result's* legacy alias, and stays.)
        endpoint: ds.endpoint ?? ds.url ?? trimmed,
        timeout: ds.timeout ?? prev.timeout,
        description: ds.description ?? prev.description,
        parameterRows: parsedRows.length > 0 ? parsedRows : prev.parameterRows,
        ...(inferredType === 'ORTB'
          ? { ortbBodyTemplate: seedOrtbBodyTemplate(prev.ortbBodyTemplate, PROTOCOL_VERSIONS.ORTB.default) }
          : {}),
      }));
      setDetectedType(inferredType);
      setQuickFillPreset(result.template?.name ?? null);
      setQuickFillWarnings(Array.isArray(result.warnings) ? result.warnings : []);
      setQuickFillSuccess(true);
    } catch (err: unknown) {
      const detail = err instanceof Error ? err.message : '';
      setQuickFillError(
        detail
          ? `Could not parse URL: ${detail}`
          : 'Could not parse URL. Ensure it starts with https:// and is a valid ad tag.',
      );
    } finally {
      setQuickFillLoading(false);
    }
  };

  /**
   * **Refuse an action and take the operator to why** — one path for Next, a jump, a step's save and
   * the final save (`5c-i` rehearsal), so none of them can refuse silently again.
   */
  const refuse = (atStep: number, error: string) => {
    setValidationError(error);
    setValidationErrorStep(atStep);
    setStep(atStep);
    setBlockedRevealRequest((n) => n + 1);
  };

  // Reveal after the commit that renders the step, so the target exists. One frame for a nested
  // editor still laying out, the same allowance `useRevealOnOpen` makes.
  React.useEffect(() => {
    if (blockedRevealRequest === 0) return;
    const frame = requestAnimationFrame(() => {
      const target = blockingProblemTarget(scrollBodyRef.current);
      if (!target) return;
      // `'center'`, not `'nearest'`: the footer is pinned over the bottom of the pane, so a target
      // revealed at the nearest edge can land underneath it.
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [blockedRevealRequest]);

  const handleNext = () => {
    const err = validateStep(step, form, steps, nameCandidates);
    if (err) {
      refuse(step, err);
      return;
    }
    setValidationError(null);
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    setValidationError(null);
    setStep((s) => s - 1);
  };

  const handleStepJump = (targetStep: number) => {
    if (targetStep === step) return;
    setSubmitError(null);

    if (targetStep < step) {
      setValidationError(null);
      setStep(targetStep);
      return;
    }

    const blocked = firstBlockingStep(form, steps, step, targetStep, nameCandidates);
    if (blocked) {
      refuse(blocked.step, blocked.error);
      return;
    }

    setValidationError(null);
    setStep(targetStep);
  };

  /**
   * Stage the edit a diagnosis Fix points at (#895). Every target sits before
   * Review, so the jump is always backwards and needs no step validation — the
   * operator is being sent to the field, not walked through the flow.
   */
  const handleDiagnosisFix = (fix: DiagnosisFix) => {
    // Every target is a phase that exists — the simplified flow's missing
    // mapping steps were the only reason this ever had to branch (#896).
    handleStepJump(WIZARD_STEP_FOR_FIX_TARGET[fix.target]);
  };

  /**
   * "Map from response" (#866, #992) — reuse the Prove & map step's capture, and only test
   * when there is none.
   *
   * This used to be a second driver call of its own (`handleFetchSampleResponse`),
   * sitting a few hundred pixels below the Prove & map step's own Test button and hitting
   * the same endpoint with the same payload: two real auctions for one activity. It
   * also passed no `context`, so the mapping was derived from a response to a
   * *different request* than the one the operator had just validated — #927's
   * "proven and simulated must describe the same thing", inside a single screen.
   */
  /**
   * Evidence from the Prove & map step, replayed on save (#994).
   *
   * The ephemeral test persists nothing, so without this the sequence edit → Prove →
   * Save ended with the badge reading `needsReproving` *because of* the change that
   * had just been proven: the observation was discarded, then the config diff was
   * noticed. Proving was the one action guaranteed to lose the proof.
   *
   * Both halves are the server's own — the console echoes them and derives no state.
   * The server refuses the evidence if the fingerprint no longer matches what is
   * being written, so proving and then editing again records nothing, correctly.
   */
  const testEvidence = (): DemandSourceTestEvidence | undefined => {
    const outcome = reviewQuickTestResult?.outcome;
    const fingerprint = reviewQuickTestResult?.testedFingerprint;
    if (!outcome || !fingerprint) return undefined;
    return { outcome, fingerprint };
  };

  const responseFromReviewTest = (): string | null => {
    const body = reviewQuickTestResult?.body as { capture?: DriverCapture } | undefined;
    const text = body?.capture?.response?.body;
    return typeof text === 'string' && text.trim() ? text : null;
  };

  const handleMapFromResponse = async (): Promise<string> => {
    const existing = responseFromReviewTest();
    if (existing) return existing;

    if (!form.endpoint.trim()) {
      throw new Error('Set the URL / Endpoint before mapping from a response.');
    }
    const payload = buildPayload();
    if (!payload) {
      throw new Error('Could not build the request from the current configuration.');
    }

    const result = await demandSourceService.quickTestConfig(
      {
        ...payload,
        id: editSource?.id,
      } as Parameters<typeof demandSourceService.quickTestConfig>[0],
      testContext.selection,
    );

    const body = result.body as { capture?: DriverCapture } | undefined;
    const capture = body?.capture;
    if (!capture?.response) {
      const detail = (body as { detail?: unknown } | undefined)?.detail;
      throw new Error(
        detail
          ? `The demand source call failed: ${String(detail)}`
          : `The demand source call failed (HTTP ${result.status}).`,
      );
    }
    return capture.response.body;
  };

  /**
   * **What the mapping on screen finds in a sample** (`#1144`). The source is the form as it stands,
   * with the editor's document laid over it, so the preview answers for the draft rather than for
   * the last save. Needs no endpoint: nothing is sent.
   */
  const handlePreviewSample = async (
    sample: string,
    responseMapping: MappingDocument,
    targets: readonly string[],
  ): Promise<ResponseMappingPreview> => {
    const payload = buildPayload();
    if (!payload) throw new Error('Could not read the current configuration.');
    return demandSourceService.previewResponseMapping(
      { ...payload, id: editSource?.id, responseMapping } as Parameters<
        typeof demandSourceService.previewResponseMapping
      >[0],
      sample,
      targets,
      testContext.selection,
    );
  };

  // Signature matches QuickTestPanel's `onTest`; the id is unused here because the
  // wizard tests the live form config, not a stored source (#872).
  const handleReviewQuickTest = async (_id: string | number | undefined, context: TestRunContext) => {
    const targetUrl = form.endpoint.trim();
    if (!targetUrl) {
      setReviewQuickTestError(new Error('URL / Endpoint is required.'));
      setReviewQuickTestResult(null);
      return;
    }

    setReviewQuickTestLoading(true);
    setReviewQuickTestError(null);
    try {
      // Ephemeral driver-based test (#872): the current form config is sent in
      // the request body, so the real driver runs (param/macro resolution,
      // response mapping) and returns the #867 request/response capture for
      // BOTH VAST and ORTB — in create mode as well as edit mode. Nothing is
      // persisted, and unlike the id-based test this always reflects unsaved
      // edits rather than the last-saved config.
      const payload = buildPayload();
      if (!payload) {
        setReviewQuickTestResult(null);
        return;
      }
      // No cast — `buildPayload` is annotated now, which is also what proves the
      // payload actually carries `method`/`endpoint`/`params`.
      setReviewQuickTestSource(payload);

      const result = await demandSourceService.quickTestConfig(
        {
          ...payload,
          id: editSource?.id,
        } as Parameters<typeof demandSourceService.quickTestConfig>[0],
        context,
      );
      setReviewQuickTestResult(result);
      if (!result.ok) {
        setReviewQuickTestError(new Error(`Test failed with status ${result.status}.`));
      }
    } catch (err: unknown) {
      setReviewQuickTestError(err instanceof Error ? err : new Error('Quick test failed.'));
      setReviewQuickTestResult(null);
    } finally {
      setReviewQuickTestLoading(false);
    }
  };

  /**
   * The record this wizard will save, in the DTO's vocabulary.
   *
   * **The return type is load-bearing.** It was inferred, and the builder emitted the
   * console's pre-#936 names — `type`, `url`, `parameters` — long after the mappers
   * that used to translate them were deleted. Nothing failed to compile: an inferred
   * object literal returned from a function is no longer "fresh" at the call site, so
   * excess-property checking never ran, and `Partial<DemandSource>` accepts a payload
   * that happens to be missing `method` and `endpoint`.
   *
   * What that cost, in the four days it was live:
   *
   * - **Editing any OpenRTB source reset it to VAST.** The backend derives
   *   `method` from `source.method`, which was absent, and
   *   `normalizeDemandSourceMethod(undefined)` is `'VAST'` — #891's exact failure,
   *   through a different door.
   * - **Creating a source saved no endpoint**, since `endpoint` was never sent.
   * - **The Prove phase's test always failed** with "Demand source endpoint (URL) is
   *   required": `quickTestConfig` forwards `source.endpoint`, which was undefined.
   *
   * Annotating the return type is what makes those three compile errors instead of
   * behaviour. Keep the annotation.
   *
   * `asTemplate` is a **parameter** rather than a read of state (`ST12` item 2): the footer's two
   * acts decide it at the press, and a `setState` in the same tick as a submit is read back stale.
   * It defaults to the entry point's standing claim, which is what every other caller wants.
   */
  const buildPayload = (asTemplate: boolean = isTemplateArtifact): Partial<DemandSource> | null => {
    let headers: Record<string, unknown> | undefined;
    let headersProvenance: Record<string, ValueOrigin> | undefined;
    let params: Record<string, unknown> | undefined;
    let customPixels: CustomPixel[] | undefined;
    let terms: DemandSourceTermsPayload | undefined;

    // No mode gate (#896). Every editor is on the one flow, so everything it
    // shows is serialized — a value the operator could see and edit must be a
    // value that gets saved. The old `if (advancedMode)` meant a source authored
    // in simplified mode silently discarded headers, parameters, pixels and terms
    // it had inherited from a preset or an earlier edit.
    try {
      const headersObj = rowsToHeadersObject(form.headerRows);
      headers = Object.keys(headersObj).length > 0 ? headersObj : undefined;
      // The side-car travels with the headers or the chips are lost on save (R4b).
      // Beside them, never inside: `headers` goes to the driver as request headers.
      const headersProv = rowsToHeadersProvenance(form.headerRows);
      headersProvenance = Object.keys(headersProv).length > 0 ? headersProv : undefined;
      // Always serialize — even when every row was deleted — so CLEARING all
      // parameters sends an explicit `{fields:{}}` the update path can act on. Sending `undefined` here reads as "leave parameters
      // untouched", which silently resurrects the deleted rows.
      params = rowsToParamsDto(form.parameterRows) as Record<string, unknown>;
      terms = termsToPayload(form.terms);
      const pixels = rowsToDtoCustomPixels(form.pixelRows);
      customPixels = pixels.length ? pixels : undefined;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Could not parse the request configuration.';
      setSubmitError(message);
      return null;
    }

    // Inbound response mapping (Universal Mapping Model, #866). Only persist a
    // document that actually has entries.
    const responseMapping =
      form.responseMapping && form.responseMapping.entries.length > 0 ? form.responseMapping : undefined;

    const ortbOutboundMapping =
      form.method === 'ORTB' ? sanitizeOrtbOutboundMapping(form.ortbOutboundMapping) : undefined;

    // The shared projection (`prebidConfigFromForm`), which the detail page's Prebid section also saves
    // through. `null` is *off*: on an edit, leaving the key out kept a config the operator switched off.
    let prebidConfig: PrebidConfig | null | undefined;
    try {
      const stated = prebidConfigFromForm(form);
      prebidConfig = stated ?? (isEditMode && form.method === 'ORTB' ? null : undefined);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Could not build Prebid configuration.';
      setSubmitError(message);
      return null;
    }

    return {
      name: form.name.trim(),
      // **Authored, not generated** (A7, `#1147`). Sent on create; the backend no longer mints one.
      // On edit it is sent unchanged and refused if it moved — the id is frozen after creation, so
      // this is the belt behind the disabled input.
      id: form.id.trim(),
      method: form.method,
      endpoint: form.endpoint.trim(),
      description: form.description.trim() || undefined,
      timeout: form.timeout,
      // `DemandSourceDto.protocolVersion` (`#1135`) — driver configuration on its own field, never a
      // request parameter. Persisted from every flow so a 2.5 source stays 2.5 (the tester's 2.5/2.6
      // trap). Custom has no protocol version.
      protocolVersion:
        PROTOCOL_VERSIONS[form.method].hasSelector && form.protocolVersion ? form.protocolVersion : undefined,
      // Omitted when the operator has not declared one. `undefined` is the optimistic
      // default and it must stay *absent* rather than being written as `{ mode: 'pod' }` —
      // a stored value would claim a declaration nobody made, which is the thing #959 says
      // we never assert on a partner's behalf.
      podCapability: form.podMode ? { mode: form.podMode, maxImpressions: form.podMaxImpressions } : undefined,
      // Price, weight and max rounds are terms since `3c-i`, and travel in `terms` below.
      enabled: form.enabled,
      ownerId: form.ownerId || undefined,
      // `owner` is a label and `createdBy` is authorship, which the server owns and never changes
      // (`#900`). The wizard sent `createdBy: form.owner`, so saving an owned source rewrote its author.
      visibility: form.visibility,
      headers,
      headersProvenance,
      params,
      terms,
      // Always sent, `{}` when nothing is stated (`5c-i`) — an absent key on an update reads as *leave the
      // stored restrictions alone*, so clearing the last one would silently keep it.
      restrictions: restrictionsToPayload(form.terms),
      responseMapping,
      ortbOutboundMapping: ortbOutboundMapping ?? undefined,
      ortbBodyTemplate:
        form.method === 'ORTB' && form.ortbBodyTemplate && Object.keys(form.ortbBodyTemplate.body ?? {}).length > 0
          ? form.ortbBodyTemplate
          : undefined,
      customPixels,
      pixelSamplingRate: buildPixelSamplingRate(),
      prebidConfig,
      /**
       * **A template, when that is what the operator came here to build** (`TM-C`, `#1171`).
       *
       * `undefined` rather than `false` for an ordinary source: absent is the column's default and
       * stating `false` would claim a decision nobody made — the same rule `podCapability` follows
       * two fields up.
       *
       * It carries **no readiness** and nothing here has to remember that: `applyValidationOnSave`
       * drops it for any template, so a wizard that ran its Prove test before the operator decided
       * this was a template cannot leave a badge behind (`TM6`/`E5`).
       */
      isTemplate: asTemplate ? true : undefined,
      // **No `authoring` key.** The console stopped writing one with `#1184`: every claim it used
      // to assemble now rides on the thing it is about — `optional` on a parameter row, `$optional`
      // and `$suppress` on a body leaf's node. What remains in the column is a vendor preset's own
      // declaration, and *replace, or keep what is stored* means omitting the key keeps it.
    };
  };

  /**
   * Resolved-request preview for the Configure Request step (R4a/R4e). Declared after
   * `buildPayload` because it resolves the same payload the test would send — the
   * preview is only trustworthy if it describes the request that would actually go
   * out, so it must not assemble its own.
   */
  const requestPreview = useRequestPreview(
    buildPayload,
    testContext.selection,
    true,
    // Endpoint and protocol are edited on the Connection step, where no Apply fires.
    `${form.endpoint ?? ''}|${form.method ?? ''}`,
  );

  /**
   * Percentage input → fraction (0–1). Only emitted when advanced + below 100%
   * (the DTO default is 1:1); an empty/100/invalid value stores nothing.
   */
  const buildPixelSamplingRate = (): number | undefined => {
    const raw = form.pixelSamplingRate.trim();
    if (raw === '') return undefined;
    const pct = Number(raw);
    if (!Number.isFinite(pct) || pct >= 100) return undefined;
    return Math.max(0, pct) / 100;
  };

  const handleSaveCurrentStep = () => {
    if (!isEditMode || !editSource) return;
    setSubmitError(null);

    const err = validateStep(step, form, steps, nameCandidates);
    if (err) {
      refuse(step, err);
      return;
    }

    const payload = buildPayload();
    if (!payload) return;

    updateMutation.mutate(
      { id: editSource.id, source: payload, testEvidence: testEvidence() },
      {
        onSuccess: () => {
          setValidationError(null);
          setLastSavedSnapshot(currentFormSnapshot);
          setShowSavedBadge(true);
          window.setTimeout(() => setShowSavedBadge(false), 1800);
        },
        onError: (error: unknown) => {
          const msg = error instanceof Error ? error.message : 'Failed to update demand source.';
          setSubmitError(msg);
        },
      },
      editSource.name,
    );
  };

  /**
   * Save — and, in create mode, **which of the two things the footer offers** (`ST12` item 2).
   *
   * The outcome is an argument rather than a read of `saveAsTemplate` state, because the press is
   * what decides it: setting state and submitting in one tick reads the old value back, and a wizard
   * that created a source when the operator pressed *Create Template* is `TM9`'s one prohibition —
   * one gesture must never silently do the other.
   */
  const handleCreate = (
    asTemplate: boolean = isTemplateArtifact,
    /**
     * The name to save under, where the act proposed one (`ST12` item 3) — *Create Template*, which
     * is the only place on this phase that changes what is being made. Absent means the form's,
     * which is every other path.
     */
    nameOverride?: string,
  ) => {
    setSubmitError(null);
    // **Every step is judged before the final save** (`5c-i` rehearsal). This called no validation at
    // all, so page 4's own checks — the name, the id, the terms and the restrictions — ran on `Next`
    // and on a middle step's *Save Changes*, and never on the one button that saves from page 4 itself.
    // A minimum ad length above the maximum saved straight through. Walks every step, as a jump
    // forward does, and lands on the first one with a problem.
    const blocked = firstBlockingStep(form, steps, 0, steps.length, nameCandidates);
    if (blocked) {
      refuse(blocked.step, blocked.error);
      return;
    }
    setValidationError(null);
    const payload = nameOverride ? { ...buildPayload(asTemplate), name: nameOverride } : buildPayload(asTemplate);
    if (!payload) return;

    if (isEditMode && editSource) {
      updateMutation.mutate(
        { id: editSource.id, source: payload, testEvidence: testEvidence() },
        {
          onSuccess: () => onClose(),
          onError: (err: unknown) => {
            const msg = err instanceof Error ? err.message : 'Failed to update demand source.';
            setSubmitError(msg);
          },
        },
        editSource.name,
      );
    } else {
      createMutation.mutate(
        {
          source: payload,
          /**
           * **Create from template means copy** (Todd, 2026-08-26, `TM9`) — `origin` records where
           * this came from and `parentId` is never set, so the source does not track the template.
           *
           * Deliberate, and time-bounded: _"especially before we get any kind of versioning… may
           * revisit in the future if versioning — and holding versions in the db — does land"_. An
           * extended copy keeps resolving through its parent, so a template edited a month later
           * would silently rewrite live sources with no version to pin them to.
           *
           * `selectedTemplateId` rather than the arrival parameter: the operator may have switched
           * templates inside the wizard, and what this was built from is the last one applied.
           */
          fromTemplateId: selectedTemplateId || undefined,
        },
        {
          onSuccess: (created) => onClose(created),
          onError: (err: unknown) => {
            const msg =
              err instanceof Error
                ? err.message
                : asTemplate
                  ? 'Failed to create template.'
                  : 'Failed to create demand source.';
            setSubmitError(msg);
          },
        },
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      requestClose();
      return;
    }
    if (e.key === 'Enter') {
      // Don't intercept Enter when focus is inside a textarea (newlines), a button
      // (which would double-fire its click), a select, or a rich text editor such
      // as the CodeMirror raw JSON editor (contenteditable) — where Enter must add
      // a newline or accept an autocomplete suggestion.
      const el = e.target as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (tag === 'textarea' || tag === 'button' || tag === 'select') return;
      if (el.isContentEditable || el.closest?.('.cm-editor')) return;
      // A `role="button"` element behaves like a button and has its own Enter
      // action (the parameter and pixel editors' expression toggles are divs).
      // Excluding only real `<button>` tags let Enter both fire the toggle and
      // advance the phase.
      if (el.closest?.('[role="button"]')) return;
      if (step < lastStep) handleNext();
      if (step === lastStep && !createMutation.isPending && !updateMutation.isPending) handleCreate();
    }
  };

  const handleDialogScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setIsFooterElevated(e.currentTarget.scrollTop > 0);
  };

  const currentStepLabel = steps[step];

  /**
   * **The result on screen describes an earlier form** (`#947`). A result now follows the operator
   * across three pages, so an edit between the test and the page they are reading is the ordinary
   * case. Compared on the payload the test snapshotted (`reviewQuickTestSource`), which is the
   * request that ran, against the one the form would send now.
   *
   * **Computed here, not beside `buildPayload`**: calling it during render reaches helpers declared
   * after it (`buildPixelSamplingRate`), which are still in the temporal dead zone there. The
   * wizard crashed on the first render after a test.
   */
  const reviewResultIsStale =
    reviewQuickTestSource !== null && JSON.stringify(buildPayload()) !== JSON.stringify(reviewQuickTestSource);

  /**
   * **A control's problem, gated on the operator having met it** (`ST12` item 4).
   *
   * Recomputed on every render from the live form, so it disappears the moment the value is fixed —
   * the same property the footer block has, and for the same reason: a message that outlives what it
   * describes is a claim about a form that has moved on.
   *
   * `validationError !== null` is *a save or a jump was refused*, which is this surface's
   * `submitted`. It is deliberately not per-field: a refusal is the operator asking to be told
   * everything that is wrong.
   */
  /** What the footer offers at the last phase — see {@link footerSaveActions}. */
  const saveActions = footerSaveActions({ isEditMode, isTemplateArtifact });
  const stepFieldProblems = stepProblems(step, form, steps, nameCandidates);
  const problemFor = React.useCallback(
    (field: WizardFieldKey): string | undefined => {
      if (!touchedFields.has(field) && validationError === null) return undefined;
      return stepFieldProblems.find((problem) => problem.field === field)?.message;
    },
    [touchedFields, validationError, stepFieldProblems],
  );
  /**
   * **What is wrong and does not block, per field** (`#1456`) — drawn beside each control, never gated
   * on touched: a finding is information about the value in the box, not a refusal, so it is true on a
   * pristine form as much as a typed one. A template, created or edited, is exempt: an unfilled slot is
   * its shape.
   */
  const fieldFindings = formFieldFindings(form, isTemplateArtifact);
  const footerBlock =
    validationError !== null && validationErrorStep !== null
      ? validateStep(validationErrorStep, form, steps, nameCandidates)
      : null;

  /**
   * The readiness this session's test observed. Null until a test has been run,
   * in which case `ReadinessClaim` falls back to the readiness already stored on
   * the source (#897) — never to configuration (R2, design system).
   */
  const reviewOutcome: TestOutcome | null = React.useMemo(
    () =>
      reviewQuickTestResult || reviewQuickTestError
        ? // Server-derived (#897) when the admin API answered; the local
          // derivation covers only the case it could not report.
          (reviewQuickTestResult?.outcome ??
          deriveTestOutcome({
            result: reviewQuickTestResult,
            requestError: reviewQuickTestError,
            source: reviewQuickTestSource,
          }))
        : null,
    [reviewQuickTestResult, reviewQuickTestError, reviewQuickTestSource],
  );

  return (
    <>
      {/* Blocking, above the wizard's own backdrop (#1129). Every route out is a deliberate click or
          a second Escape **on this dialog**, so there is nothing armed and invisible behind the
          operator as they keep working. Cancel holds the focus, because this appears in answer to a
          keystroke and the next one is often already on its way. */}
      {closeConfirm && (
        <ConfirmDialog
          title="Unsaved changes"
          body={
            isEditMode
              ? 'Your edits to this demand source have not been saved. Exit without saving?'
              : 'This demand source has not been created yet. Exit without saving?'
          }
          confirmLabel="Exit without saving"
          cancelLabel="Keep editing"
          /* **The third answer, offered where the operator meets the question** (`ST12` item 2).
             *An exit is not a decision* gives them save or discard; *save it as a copy* keeps both,
             and it is what they wanted when they hit this prompt on an existing source they did not
             want to overwrite. On the phase as well, so it is reachable by somebody who thought of
             it first — but an operator only meets a capability they did not know about at the moment
             something blocks them. */
          alternative={
            isEditMode && editSource
              ? {
                  label: 'Save as a copy…',
                  onSelect: () => {
                    setCloseConfirm(false);
                    setCloneError(null);
                    setSaveAsOpen(true);
                  },
                }
              : undefined
          }
          onCancel={() => setCloseConfirm(false)}
          onConfirm={() => {
            setCloseConfirm(false);
            onClose();
          }}
        />
      )}

      {/* **What a template made from this would carry** (`TM5`, `#1171`), at the press rather than
          on the phase (`ST12` item 2). It was a panel standing above the identity fields for as long
          as a radio said *template*; with the choice on the act, this is the moment the operator can
          still go back and take something out — which is what Todd asked for: *"up to them to decide
          what they want to remove before the save."*

          A confirm rather than a second dialog type, so the answer is one blocking question with two
          deliberate ways out and nothing armed behind it (`#1129`). */}
      {createTemplateName !== null && (
        <ConfirmDialog
          title="Create a template from this?"
          tone="neutral"
          body={
            <>
              {/* **The name changes direction here, so it is proposed here** (`ST12` item 3). The
                  operator named a *source*; this is the one act on the phase that makes it a
                  template, and Todd's rule is that the word goes on the end — once. Editable,
                  because the proposal is a guess about a name only they know. */}
              <Box mb={3}>
                <Text fontSize="xs" fontWeight="600" color="fg.default" mb={1}>
                  Template name
                </Text>
                <Input
                  aria-label="Template name"
                  value={createTemplateName}
                  onChange={(e) => setCreateTemplateName(e.target.value)}
                  size="sm"
                  borderColor={createTemplateName.trim() ? 'border.default' : 'error.border'}
                  aria-invalid={createTemplateName.trim() ? undefined : true}
                  _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
                />
                <Text fontSize="xs" color="fg.muted" mt={1}>
                  It will be saved under the ID{' '}
                  <Text as="span" fontFamily="mono">
                    {form.id}
                  </Text>
                  .
                </Text>
              </Box>
              <Text fontSize="xs" color="fg.muted" mb={2}>
                {TEMPLATE_DISCLOSURE_HEADLINE}
              </Text>
              <TemplateDisclosureList
                lines={describeTemplateContents(buildPayload(true) as DemandSource)}
                emptyNote="Nothing configured yet — this template would start an empty demand source."
              />
              <Text fontSize="xs" color="fg.muted" mt={3}>
                It never serves a request and is never selected by a strategy. It appears under Templates.
              </Text>
            </>
          }
          confirmLabel="Create template"
          cancelLabel="Go back"
          onCancel={() => setCreateTemplateName(null)}
          onConfirm={() => {
            const name = createTemplateName.trim();
            // A blank name is the one thing this dialog can refuse; everything else was judged before
            // it opened. Keeping it open is the refusal — the box is right there.
            if (!name) return;
            setCreateTemplateName(null);
            handleCreate(true, name);
          }}
        />
      )}

      {/* **Save as…** — the one clone gesture, reached from an open editor (`ST12` item 2, `Q6`).
          `subject="unsaved"` is a claim the request has to make good on, which is why the draft is
          sent: the server copies the stored record unless it is handed one. */}
      {saveAsOpen && editSource && (
        <CloneArtifactOverlay
          instanceNoun="demand source"
          sourceName={form.name || editSource.name}
          // Editing a template: the copy is one too, so the proposal keeps the word and lets the
          // relation name the copy (`ST12` item 3).
          sourceIsTemplate={editSource.isTemplate === true}
          // What the operator is looking at, not what is stored — and it says so.
          subject="unsaved"
          existingNames={nameCandidates ?? []}
          offersInheritance
          templateOutcome={editSource.isTemplate ? null : { initial: false, options: DEMAND_SOURCE_OUTCOMES }}
          disclosure={describeTemplateContents(buildPayload(true) as DemandSource)}
          isPending={cloneMutation.isPending}
          error={cloneError}
          onCancel={() => setSaveAsOpen(false)}
          onConfirm={(copy) => {
            setCloneError(null);
            // Judged exactly as a save is: a copy that could not be saved is a copy nobody wanted.
            const blocked = firstBlockingStep(form, steps, 0, steps.length, nameCandidates);
            if (blocked) {
              setSaveAsOpen(false);
              refuse(blocked.step, blocked.error);
              return;
            }
            const document = buildPayload(copy.asTemplate === true);
            if (!document) return;
            cloneMutation.mutate(
              { id: editSource.id, copy, document },
              {
                onSuccess: (created) => {
                  setSaveAsOpen(false);
                  // Hand the copy back rather than the original: *what the wizard saved decides where
                  // the operator should land* (`TM1a`), and what it saved here is the copy.
                  onClose(created);
                },
                // Kept on screen: the operator's name and id are still in the dialog, so correcting a
                // taken one costs an edit instead of re-deciding.
                onError: (err: unknown) =>
                  setCloneError(err instanceof Error ? err.message : 'Could not save this as a copy.'),
              },
            );
          }}
        />
      )}

      {/* The blast-radius confirm (`P4`), raised by the guarded update hook when this save would
          reach a source built from this one. Beside the unsaved-changes confirm rather than inside
          the pane, for the same reason that one is: it must sit above the wizard's own backdrop, and
          it must not be reachable by the wizard's Escape handling. Only one of the two is ever on
          screen — this one appears in answer to Save, that one in answer to leaving. */}
      {updateMutation.impactDialog}

      {/* Full-screen backdrop */}
      <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} display="flex" p={{ base: 0, md: 4 }}>
        {/* Full-page dialog — two-pane: persistent step rail + right pane.
          Fills the viewport (no maxW cap); the content pane constrains its own
          field widths where readability needs it, rather than the shell. */}
        <Flex
          bg="bg.card"
          borderRadius={{ base: '0', md: '12px' }}
          boxShadow="0 8px 32px rgba(0,0,0,0.18)"
          w="100%"
          h="100%"
          overflow="hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="wizard-title"
          onKeyDown={handleKeyDown}
        >
          {/* Left step rail */}
          <StepRail currentStep={step} steps={steps} onStepClick={handleStepJump} severityForStep={severityForStep} />

          {/* Right pane */}
          <Flex direction="column" flex="1" minW={0}>
            {/* Header */}
            <Flex
              align="center"
              justify="space-between"
              px={6}
              py={4}
              borderBottomWidth="1px"
              borderColor="border.default"
              flexShrink={0}
            >
              {/* ── The title bar (#1122 · #1115) ─────────────────────────────
                *"Before the source is named, the 'title bar' could just show 'ORTB Demand Source -
                Publica Template'."* The wizard is where identity matters most and was stated least:
                for the first two steps there is no name at all, while the operator finishes a
                configuration whose protocol and preset they chose several screens ago.

                Rendered through `artifactTitle` + `ArtifactIdentity`, the same projection the list
                row and the detail header use — identity is *"an implicit part of the name"*, so a
                surface that composed its own string here would be the one that disagrees.

                The dirty state sits here too, beside the name, because *"where am I and what have I
                done?"* is one question. It reuses the wizard's own `hasUnsavedChanges` rather than
                introducing a third definition of dirty (#1122's stated correctness claim): the two
                that exist today — `ConfigPage`'s snapshot compare and `StrategySelectorPage`'s
                field-by-field check — give different answers on the same edit, and unifying them is
                that issue's work, not a thing to guess at here. */}
              <HStack gap={2.5} minW={0}>
                {/* The verb, kept because the title no longer carries it. This header used to read
                  "Edit — {name}", and dropping the word entirely would make a named draft and a
                  saved source read identically at the moment the difference matters most: one of
                  them does not exist yet. It is an eyebrow rather than a prefix so the *name* stays
                  the title — the thing every other surface renders. */}
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="fg.muted"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  flexShrink={0}
                >
                  {isEditMode ? 'Editing' : 'New'}
                </Text>
                <Heading id="wizard-title" size="md" color="fg.title" truncate>
                  {artifactTitle(form, wizardTypeLabel)}
                </Heading>
                <ArtifactIdentity
                  artifact={isEditMode ? editSource : undefined}
                  lookupParentName={lookupParentName}
                  parentIsTemplate={parentIsTemplate}
                  appliedTemplateName={appliedTemplateName}
                  appliedTemplateUrl={appliedTemplateUrl}
                  size="md"
                />
                {hasUnsavedChanges && (
                  <Text fontSize="xs" color="warning.ink" fontWeight="600" whiteSpace="nowrap">
                    ● Unsaved
                  </Text>
                )}
              </HStack>
              <HStack gap={2}>
                {/* No Advanced toggle (#896 · R1). Depth follows the endpoint, not a
                mode chosen before the endpoint's requirements are visible. */}
                <Button
                  size="sm"
                  variant="ghost"
                  color="fg.muted"
                  _hover={{ bg: 'bg.canvas' }}
                  onClick={requestClose}
                  aria-label="Close wizard"
                >
                  ✕
                </Button>
              </HStack>
            </Flex>

            {/* Scrollable content — uncapped, fills the right pane */}
            <Box ref={scrollBodyRef} flex="1" minH={0} overflowY="auto" px={6} py={5} onScroll={handleDialogScroll}>
              {currentStepLabel === 'Connection' && (
                <VStack gap={5} align="stretch">
                  <PhaseConnection
                    problemFor={problemFor}
                    endpointFinding={fieldFindings.endpoint ?? null}
                    onFieldBlur={markFieldTouched}
                    form={form}
                    onChange={onChange}
                    onQuickFill={handleQuickFill}
                    quickFillLoading={quickFillLoading}
                    quickFillError={quickFillError}
                    quickFillPreset={quickFillPreset}
                    quickFillSuccess={quickFillSuccess}
                    quickFillWarnings={quickFillWarnings}
                    isEditMode={isEditMode}
                    detectedType={detectedType}
                    onUrlChange={handleUrlChange}
                    onUrlBlur={handleUrlBlur}
                    templates={templates}
                    templatesLoading={templatesLoading}
                    templatesError={templatesError}
                    selectedTemplateId={selectedTemplateId}
                    onSelectTemplate={applyTemplate}
                  />
                  {/* Version and timeout are part of identifying the connection, not of
                  decorating the request (#995 · R13) — and endpoint + protocol + these
                  two are exactly what the detail page's Connection section shows. */}
                  <ConnectionSettings
                    form={form}
                    onChange={onChange}
                    problemFor={problemFor}
                    onFieldBlur={markFieldTouched}
                    timeoutFinding={fieldFindings.timeout ?? null}
                  />
                </VStack>
              )}
              {/* **One test panel, above pages 2–4** (`#947`, Todd 2026-09-28). The loop was one click from
              Prove & map back to an editing page (a Fix) and several clicks forward again. Now the test is
              on every page that can change what it tests, and a result carries between them, marked when
              the form has moved on since. Next never runs a test: it arrives with the button ready. */}
              {TESTABLE_PHASES.has(currentStepLabel) && (
                <Box mb={5}>
                  <QuickTestPanel
                    sourceId={form.name || 'draft-source'}
                    sourceName={form.name || 'Draft Source'}
                    result={reviewQuickTestResult}
                    isLoading={reviewQuickTestLoading}
                    error={reviewQuickTestError}
                    source={reviewQuickTestSource}
                    onFix={handleDiagnosisFix}
                    onTest={handleReviewQuickTest}
                    testContext={testContext.selection}
                    contextPicker={<TestContextPicker state={testContext} disabled={reviewQuickTestLoading} />}
                    preview={{
                      request: requestPreview,
                      describes: 'The request this form would send, as it stands now.',
                    }}
                    resultIsStale={reviewResultIsStale}
                    onClose={() => {
                      setReviewQuickTestResult(null);
                      setReviewQuickTestError(null);
                      setReviewQuickTestSource(null);
                    }}
                  />
                </Box>
              )}
              {currentStepLabel === 'Configure Request' && (
                <StepRequestDecoration
                  form={form}
                  onChange={onChange}
                  prebidFinding={fieldFindings.prebid ?? null}
                  isTemplate={isTemplateArtifact}
                  ortbParamNote={ortbParamNote}
                  preview={requestPreview}
                  inheritance={wizardInheritance ?? null}
                  baseline={storedRows}
                  revert={wizardRevert}
                  fromTemplate={copiedFrom}
                  onSaveExpressionToLibrary={saveExpressionToLibrary}
                  templateOptional={templateOptional}
                />
              )}
              {/* Prove & map — the test and the response mapping together. Response
              mapping belongs here rather than in its own step: it is a reading
              of a payload the operator is looking at (#896 · R1), and #992 made
              testing and mapping one action. The phase is named for both (#995 · R15)
              rather than split, which would put a boundary through one activity. */}
              {currentStepLabel === 'Prove & map' && (
                <VStack gap={5} align="stretch">
                  <StepResponseMapping
                    form={form}
                    sharedPixels={sharedPixels}
                    proxyBaseUrl={systemDefaults?.proxyUrl}
                    onChange={onChange}
                    onSavePixelToLibrary={handleSavePixelToLibrary}
                    mapFrom={{ hasCapture: responseFromReviewTest() !== null, run: handleMapFromResponse }}
                    latestResponse={responseFromReviewTest()}
                    previewSample={handlePreviewSample}
                    onSaveExpressionToLibrary={saveExpressionToLibrary}
                  />
                </VStack>
              )}
              {currentStepLabel === 'Name and save' && (
                <PhaseNameAndSave
                  form={form}
                  onChange={onChange}
                  isAdmin={isAdmin}
                  isEditMode={isEditMode}
                  ownerOptions={ownerOptions}
                  outcome={reviewOutcome}
                  storedReadiness={editSource?.validation}
                  inheritance={wizardInheritance ?? null}
                  inheritedTerms={inheritedTerms}
                  stored={storedSource ?? null}
                  revert={wizardRevert}
                  // Built from the payload rather than the form, so what is disclosed is what would
                  // actually be stored — `TM5`'s statement has to describe the save, not the screen.
                  disclosure={isTemplateArtifact ? describeTemplateContents(buildPayload() as DemandSource) : null}
                  problemFor={problemFor}
                  onFieldBlur={markFieldTouched}
                />
              )}

              {/* **Wrong, and not a locked door** (2026-08-27). Rendered in the blocking error's own
                  slot and styled the same, because the severity is the same — what differs is that
                  the operator may proceed. Shown for the step they are on rather than raised on
                  Next, so it is information rather than an interruption. */}
              {stepAdvisories(step, form, steps, createAsTemplate === true).map((advisory) => (
                <Box
                  key={advisory}
                  mt={3}
                  p={3}
                  bg="error.tint"
                  borderRadius="6px"
                  border="1px solid"
                  borderColor="error.border"
                >
                  <Text fontSize="sm" color="error.solid">
                    {advisory}
                  </Text>
                </Box>
              ))}

              {/* Validation error */}
              {footerBlock && (
                <Box
                  mt={3}
                  p={3}
                  bg="error.tint"
                  borderRadius="6px"
                  border="1px solid"
                  borderColor="error.border"
                  data-wizard-validation-error
                  tabIndex={-1}
                  role="alert"
                >
                  <Text fontSize="sm" color="error.solid">
                    {footerBlock}
                  </Text>
                </Box>
              )}

              {/* Submit error */}
              {submitError && (
                <Box mt={3} p={3} bg="error.tint" borderRadius="6px" border="1px solid" borderColor="error.border">
                  <Text fontSize="sm" color="error.solid">
                    {submitError}
                  </Text>
                </Box>
              )}
            </Box>

            {/* Footer actions — anchored bottom of the right pane */}
            <Flex
              justify="space-between"
              px={6}
              py={4}
              borderTopWidth="1px"
              borderColor="border.default"
              flexShrink={0}
              bg="bg.card"
              boxShadow={isFooterElevated ? '0 -4px 12px rgba(0, 0, 0, 0.08)' : 'none'}
            >
              <Button
                size="sm"
                variant="outline"
                borderColor="border.default"
                color="fg.muted"
                _hover={{ bg: 'bg.canvas' }}
                /**
                 * **Cancel closes directly, and that is the one deliberate exception** (#1129).
                 *
                 * Escape and ✕ both go through `requestClose`, because they are *exits* — the
                 * operator is leaving, not deciding — and an exit that silently discards work is the
                 * defect this whole guard exists for. **Cancel is a decision**: it is labelled with
                 * the outcome, so prompting on it asks the same question twice, and a confirm the
                 * operator has already answered is how they learn to click through the one that
                 * matters.
                 *
                 * Written out because it otherwise reads as a missed call site.
                 */
                onClick={step === 0 ? () => onClose() : handleBack}
              >
                {step === 0 ? 'Cancel' : '← Back'}
              </Button>

              {/* **Why the last action was refused, where the button is** (`5c-i` rehearsal). Re-judged
                  from the form on every render, so it disappears the moment the value is fixed rather
                  than lingering as a claim about a form that has moved on. */}
              {footerBlock && (
                <HStack flex="1" minW={0} mx={4} gap={2} data-testid="wizard-footer-block">
                  <Text fontSize="sm" color="error.ink" lineClamp={2} title={footerBlock}>
                    {footerBlock}
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    borderColor="error.border"
                    color="error.ink"
                    flexShrink={0}
                    onClick={() => refuse(validationErrorStep!, footerBlock)}
                  >
                    Show
                  </Button>
                </HStack>
              )}

              {step < lastStep ? (
                <HStack gap={2}>
                  {isEditMode && showSavedBadge && (
                    <Text fontSize="xs" color="success.ink" fontWeight="600" mr={1}>
                      ✓ Saved
                    </Text>
                  )}
                  {isEditMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="accent.solid"
                      color="accent.solid"
                      _hover={{ bg: 'accent.subtle' }}
                      onClick={handleSaveCurrentStep}
                      disabled={!hasUnsavedChanges || updateMutation.isPending}
                    >
                      {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
                    </Button>
                  )}
                  {isEditMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="border.default"
                      color="fg.muted"
                      _hover={{ bg: 'bg.canvas' }}
                      onClick={() => onClose()}
                    >
                      Close
                    </Button>
                  )}
                  <Button size="sm" bg="accent.solid" color="white" _hover={{ bg: 'accent.ink' }} onClick={handleNext}>
                    Next →
                  </Button>
                </HStack>
              ) : (
                <HStack gap={2}>
                  {isEditMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="border.default"
                      color="fg.muted"
                      _hover={{ bg: 'bg.canvas' }}
                      onClick={() => onClose()}
                    >
                      Close
                    </Button>
                  )}
                  {/* **Save as… — the act that had no route** (`ST12` item 2, `#1359`; Todd): *open an
                      existing source, edit it, then decide you don't want to overwrite the current
                      one.* Until here the only way out of that was to discard the edits or to
                      overwrite, and the operator's real answer — keep both — was unreachable.

                      It opens the one clone gesture (`Q6`), which owns the outcome words. The label
                      names *this* act rather than the dialog's (`Q7`): from an open editor something
                      genuinely is being saved, where from a list row nothing is. */}
                  {saveActions.includes('saveAs') && editSource && (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="border.default"
                      color="fg.muted"
                      _hover={{ bg: 'bg.canvas' }}
                      onClick={() => {
                        setCloneError(null);
                        setSaveAsOpen(true);
                      }}
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      Save as…
                    </Button>
                  )}
                  {/* **Create Template, as an act rather than a radio at the top of the phase**
                      (`ST12` item 2). Offered only where the entry point did not already decide —
                      arriving from Templates → New, `isTemplateArtifact` is the standing claim and
                      the single button below says so. */}
                  {saveActions.includes('createTemplate') && (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="accent.solid"
                      color="accent.solid"
                      _hover={{ bg: 'accent.subtle' }}
                      onClick={() => {
                        // Judged before the disclosure, not after: a panel listing what a template
                        // would carry, shown over a form that cannot be saved, asks the operator to
                        // read the wrong thing. Same walk the save takes.
                        const blocked = firstBlockingStep(form, steps, 0, steps.length, nameCandidates);
                        if (blocked) {
                          refuse(blocked.step, blocked.error);
                          return;
                        }
                        setCreateTemplateName(
                          proposeArtifactName({
                            sourceName: form.name,
                            sourceIsTemplate: false,
                            targetIsTemplate: true,
                            // Not a copy — nothing is being duplicated, so there is no relationship
                            // for a suffix to name.
                            relation: null,
                            taken: nameCandidates ?? [],
                          }),
                        );
                      }}
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      Create Template
                    </Button>
                  )}
                  <Button
                    size="sm"
                    bg="accent.solid"
                    color="white"
                    _hover={{ bg: 'accent.ink' }}
                    onClick={() => handleCreate()}
                    disabled={
                      createMutation.isPending || updateMutation.isPending || (isEditMode && !hasUnsavedChanges)
                    }
                  >
                    {createMutation.isPending || updateMutation.isPending ? (
                      <HStack gap={2}>
                        <Spinner size="xs" />
                        <Text>{isEditMode ? 'Saving…' : 'Creating…'}</Text>
                      </HStack>
                    ) : isEditMode ? (
                      'Save Changes'
                    ) : isTemplateArtifact ? (
                      'Create Template'
                    ) : (
                      'Create Demand Source'
                    )}
                  </Button>
                </HStack>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </>
  );
};
